/**
 * cloud_sheets — THE LAYER CLOUDS as plane-projected sheets in the sky background (the cloud-realism work, 2026-10-07;
 * record: docs/research/2026-10-07-cloud-realism/impl-sheets.md). It generalises lib/sky/cirrus_layer (which stays as the
 * `atlas:cloud-sheets = 'legacy'` path): THREE ÉTAGES, back to front —
 *
 *   HIGH  ~8.5 km  cirrus fibratus / uncinus (painted filaments and hooks), cirrocumulus (grains, ripples),
 *                  cirrostratus (a veil with the 22° halo)
 *   MID   ~3.5 km  altocumulus (cloudlets in fields, rows and patches), altostratus (the ground-glass sun)
 *   LOW   ~0.7–1.2 km  stratocumulus (pads and rolls with seams), stratus, nimbostratus (the rain / snow deck)
 *
 * The heap clouds (cumulus → cumulonimbus, scud) stay real mesh volumes in front (engine CloudMeshField).
 *
 * WHY A SHEET AND NOT SPRITES. A layer's look IS its perspective: features shrink and pack toward the horizon, gaps that
 * are open overhead close there because cloudlet sides overlap, and the whole deck slides overhead as you walk. Pooled
 * spheres and camera-facing sprites have none of that (the baseline pictures: rice grains and a wall of stacked plates).
 *
 * THE LAWS (each one is something a later edit must not undo — impl-sheets.md §2 has the measurements):
 *  - THE SHELL. A view ray meets a SPHERICAL shell of radius R + H, never the plane y = H: a plane's horizon is at
 *    infinity (every pattern aliases into a moiré there); the shell's is 113 km away for a 1 km deck and 329 km for an
 *    8.5 km one, which is the real foreshortening. t = −c / (b + √(b² − c)) in KILOMETRES with c computed on the CPU in
 *    doubles (the naive −b + √… cancels 7 digits in float32).
 *  - THE WRAPPED ORIGIN. Absolute XZ reaches ±1,000 km and the origin rebases: the CPU integrates the camera's motion and
 *    the wind into a pattern-frame origin wrapped modulo SHEET_TILE_M (doubles); every sampling scale is that tile over
 *    an INTEGER, so the wrap is invisible. The frame turns about the CAMERA when the wind turns (a turn about the world
 *    origin would swing a sky 400 km out by kilometres per degree).
 *  - THE FOOTPRINT. Every fetch is `textureGrad` with the ANALYTIC derivative of the shell point (from dFdx/dFdy of the
 *    view direction, taken once at the top — THE LAZY-VAR LAW), so the sampler's mips and anisotropy band-limit the
 *    pattern by the pixel's real footprint on the layer. A THRESHOLDED field cannot be band-limited by its texture alone
 *    (the mean of a noise is not the mean of its threshold): past ~⅓ cell a pixel the density fades to the field's
 *    analytic MEAN thickness.
 *  - THE SLAB. A cumuliform layer is a HEIGHT FIELD (flat base, domed tops) walked at 2–6 heights from the base shell to
 *    the top shell, so the apparent cover rises with the zenith angle by itself.
 *  - LIT FROM ABOVE, SEEN FROM BELOW. The radiance of a base is TRANSMITTED light: B(τ) = (1 − e^(−τ/3.5)) / (1 + τ/8.9)
 *    of the overcast-law radiance of the light on its top — thin parts and edges bright, thick parts grey, and
 *    B(TAU_OVERCAST) = photometry's OVERCAST_GLOBAL_FRACTION exactly, so a full deck IS the engine's overcast dome
 *    (L = Lz·(1 + 2 sin θ)/3) and the ground's light stays consistent. Plus the forward lobes around the key (the silver
 *    region, the watery sun) as τ·e^(−τ), and for ice the 22° halo.
 *  - THE DECK THICKENS WITH THE LIGHT MODEL. photometry's cloudiness c dims the ground toward 7 % of the clear global;
 *    a deck's optical depth scales toward TAU_OVERCAST with the same c (and its source toward the dome's zenith over the
 *    pattern's mean B), so the sky's mean luminance under an overcast is the dome's — a structure, not a second light.
 *  - A SHEET NEVER GLOWS. At night its own light is the moon (the key flips by cloud_light's rule) and the DRAWN deck
 *    (sky_sampler.nightSkyRadiance at cloudiness 1 — the natural share and the town's light, the same term the sky
 *    shader draws behind it: under an overcast the two are equal, so nothing doubles); it hides the stars by its alpha.
 *  - WORK BEHIND A UNIFORM. Each étage is inside `If(cover > 0)` — uniform control flow, so an absent étage costs
 *    nothing and the explicit-gradient fetches stay legal on WGSL.
 *
 * NO-CYCLE RULE: parameters in, nodes out; this file imports nothing from SkyModule. The rules (`sheetsForWeather`,
 * the genus tables, the light, the noise bake, the CPU mirror) are pure — `verify:cloud-sheets` pins them.
 */
import {
  Fn, If, float, vec2, vec3, vec4, uniform, texture, dFdx, dFdy, dot, max, min, mix, exp, sqrt, pow, clamp, smoothstep,
  length, acos, saturate,
} from 'three/tsl';
import type { Texture } from 'three/webgpu';
import { E0_LUX, OVERCAST_GLOBAL_FRACTION, overcastZenithRadiance, horizonFade, lum709 } from '../photometry.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;
export interface V3 { x: number; y: number; z: number }

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// 1. Constants
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════

export const EARTH_RADIUS_M = 6_371_000;
/** The pattern frame's wrap period (m): 2¹⁰ · 3² · 5 · 7 — every sampling scale is this over an integer. */
export const SHEET_TILE_M = 322_560;
/** The nearest legal sampling scale (metres per texture tile) to a wanted one. */
export const sheetScale = (targetM: number): number => SHEET_TILE_M / Math.max(1, Math.round(SHEET_TILE_M / targetM));
/** Texture edge (texels) and the cloudlet lattice (cells per tile edge). */
export const SHEET_TEX = 512;
export const SHEET_CELLS = 16;

/** B(τ)'s two scales: the optical depth over which a cloud starts to return light, and the two-stream half depth. */
export const TAU_I = 3.5;
export const TAU_HALF = 8.9;
/** The share of the light on a cloud's top that leaves its base as diffuse radiance (overcast-law units). */
export function deckBrightness(tau: number): number { const t = Math.max(0, tau); return (1 - Math.exp(-t / TAU_I)) / (1 + t / TAU_HALF); }
/** The optical depth whose transmission is photometry's overcast fraction — the deck the light model's overcast is. */
export const TAU_OVERCAST = (() => { let lo = 20, hi = 600; for (let i = 0; i < 60; i++) { const m = (lo + hi) / 2; if (deckBrightness(m) > OVERCAST_GLOBAL_FRACTION) lo = m; else hi = m; } return (lo + hi) / 2; })();
/** The overcast dome's hue (SkyModule's `zen`: Lz × this). */
export const DECK_HUE: readonly [number, number, number] = [0.93, 0.96, 1];
/** Grazing light: the share of the key's normal illuminance a layer still takes edge-on (a set sun lights a high sheet from the side). */
export const SIDE_LIGHT = 0.18;
/** How much of the drawn night deck each étage shows (a thin ice sheet 7 km over a town returns little of its light). */
export const NIGHT_DECK_SHARE = { low: 1, mid: 0.6, high: 0.2 } as const;
/**
 * How much an étage of this cover CLOSES the sky (0 a broken sheet … 1 a near-full one). The light model's cloudiness
 * acts on an étage only through this: a few pads arriving under a grey sky are not a 118-τ lid, and an absent étage has
 * no deck to normalise (FIX 2026-10-07: weighted by the sky's cloudiness alone, an absent low étage divided the dome's
 * zenith by its vanishing mean brightness — a source of 1.06·10⁵ scene units in the bed's table, a white flash on every
 * fade-in under an overcast).
 */
export const deckClosure = (cover: number): number => { const t = Math.max(0, Math.min(1, (cover - 0.6) / 0.35)); return t * t * (3 - 2 * t); };
/** The camera fades an étage out over this many metres below its base (the background cannot be flown through). */
export const SHEET_APPROACH_FADE_M = 320;
/** The mesh formation layers these sheets replace (WeatherModule drops them wherever the sheets draw). */
export const SHEET_REPLACES_MESH: readonly string[] = ['cirrus', 'altocumulus', 'stratocumulus'];
/**
 * THE KILL SWITCH: `localStorage atlas:cloud-sheets = 'legacy'` restores the single cirrus sheet (lib/sky/cirrus_layer)
 * and the mesh field's own altocumulus / stratocumulus. Read at world entry (the sky graph is a boot-time graph); both
 * SkyModule (which sheet it builds) and the mesh formation's owner (which layers it drops) ask THIS.
 */
export function cloudSheetsMode(): 'sheets' | 'legacy' {
  try { return typeof localStorage !== 'undefined' && localStorage.getItem('atlas:cloud-sheets') === 'legacy' ? 'legacy' : 'sheets'; } catch { return 'sheets'; }
}
/** The mesh formation's layers once the sheets draw: every kind in SHEET_REPLACES_MESH dropped (a pure helper for the formation's owner). */
export function withoutSheetGenera<L extends { kind: string }>(layers: readonly L[]): L[] { return layers.filter((l) => !SHEET_REPLACES_MESH.includes(l.kind)); }
/** A lever's loose spec → a whole SheetSpec (a missing étage is absent; covers clamped). */
export function normalizeSheetSpec(o: Partial<Record<Etage, { genus?: string; cover?: number; storm?: number } | string | null>> | null | undefined): SheetSpec {
  const one = <G extends string>(v: { genus?: string; cover?: number; storm?: number } | string | null | undefined, ok: readonly string[], dflt: number): { genus: G; cover: number; storm?: number } => {
    const g = typeof v === 'string' ? v : v?.genus ?? 'none';
    if (!ok.includes(g)) return { genus: 'none' as G, cover: 0 };
    const cv = typeof v === 'object' && v && Number.isFinite(v.cover) ? Math.max(0, Math.min(1, v.cover as number)) : dflt;
    const st = typeof v === 'object' && v && Number.isFinite(v.storm) ? Math.max(0, Math.min(1, v.storm as number)) : undefined;
    return st === undefined ? { genus: g as G, cover: cv } : { genus: g as G, cover: cv, storm: st };
  };
  return {
    high: one<HighGenus>(o?.high, ['ci', 'ci-uncinus', 'cc', 'cs'], 0.4),
    mid: one<MidGenus>(o?.mid, ['ac', 'ac-undulatus', 'as'], 0.6),
    low: one<LowGenus>(o?.low, ['sc', 'sc-undulatus', 'st', 'ns'], 0.8),
  };
}

export type Etage = 'high' | 'mid' | 'low';
export type HighGenus = 'none' | 'ci' | 'ci-uncinus' | 'cc' | 'cs';
export type MidGenus = 'none' | 'ac' | 'ac-undulatus' | 'as';
export type LowGenus = 'none' | 'sc' | 'sc-undulatus' | 'st' | 'ns';
export interface SheetSpec {
  high: { genus: HighGenus; cover: number };
  mid: { genus: MidGenus; cover: number };
  /** `storm` 0..1: the deck lowers and its sagging patches deepen. */
  low: { genus: LowGenus; cover: number; storm?: number };
}
export const NO_SHEETS: SheetSpec = { high: { genus: 'none', cover: 0 }, mid: { genus: 'none', cover: 0 }, low: { genus: 'none', cover: 0 } };

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// 2. The weather rule (pure)
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════

const c01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const sstep = (a: number, b: number, v: number): number => { const t = c01((v - a) / (b - a)); return t * t * (3 - 2 * t); };
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
/** The sky's day (s of the shared wall clock) and its seed: every client, and every module that asks the rule, the same sheets. */
export const SHEET_DAY_S = 7200;
export function sheetDaySeed(nowS: number = Date.now() / 1000): number { return Math.floor(nowS / SHEET_DAY_S); }
/** A hash of an integer seed and a channel to [0, 1). */
export function sheetHash(seed: number, ch: number): number {
  let h = (Math.floor(seed) * 374761393 + ch * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

export interface SheetWeather {
  /** Cloud cover 0..1 (the resolved weather's). */
  cover: number;
  /** Precipitation 0..1 and its kind (dust is not a cloud deck). */
  precip: number;
  kind?: 'none' | 'rain' | 'snow' | 'dust';
  /** A slowly varying integer (the sky's day) so different days differ. */
  seed?: number;
}

/**
 * Which genus each étage shows and how much (the frontal sequence — WMO International Cloud Atlas, "Clouds and weather"):
 *   FAIR (cover ≤ 0.55, dry)      sparse cirrus (fibratus or uncinus by the day), some days a cirrocumulus patch or a few
 *                                 altocumulus patches; the low étage is the mesh cumulus' alone.
 *   THICKENING (cover > 0.55)     by the day, one of two skies:
 *       the WARM FRONT            a cirrostratus veil with its halo → altocumulus fields under it → altostratus (the
 *                                 watery sun) closing to an opaque grey; low stratus only at the end
 *       the DRY OVERCAST          a stratocumulus deck, broken → near-overcast (rolls on some days), thin cirrus above
 *                                 its gaps
 *   RAIN / SNOW (precip > 0.05)   nimbostratus — full, lower and deeper-mottled in a storm; the étages above are hidden
 *                                 (and cost nothing).
 * Dust is not a deck: a sandstorm keeps the dry sky of its cover.
 */
export function sheetsForWeather(w: SheetWeather): SheetSpec {
  const cover = c01(w.cover), seed = w.seed ?? 0;
  const kind = w.kind ?? (w.precip > 0 ? 'rain' : 'none');
  const precip = kind === 'dust' || kind === 'none' ? 0 : c01(w.precip);
  const u1 = sheetHash(seed, 1), u2 = sheetHash(seed, 2), u3 = sheetHash(seed, 3), u4 = sheetHash(seed, 4);
  // the precipitating deck closes in over precip 0.02 → 0.2 and hides what is above it
  const wet = sstep(0.02, 0.2, precip);
  const dry = 1 - wet;
  const out: SheetSpec = { high: { genus: 'none', cover: 0 }, mid: { genus: 'none', cover: 0 }, low: { genus: 'none', cover: 0 } };
  if (cover <= 0.55) {
    // FAIR: a fifth of the days have a bare blue sky above the cumulus
    const ci = u1 < 0.2 ? 0.04 : 0.1 + 0.4 * (cover / 0.55) * (0.6 + 0.4 * u2);
    out.high = { genus: u3 > 0.86 ? 'cc' : u2 > 0.55 ? 'ci-uncinus' : 'ci', cover: ci * dry };
    if (u4 > 0.62) out.mid = { genus: u3 > 0.5 ? 'ac-undulatus' : 'ac', cover: (0.1 + 0.22 * (cover / 0.55)) * dry };
  } else {
    const s = c01((cover - 0.55) / 0.4); // 0 at the regime's start, 1 at a closed sky (cover 0.95)
    if (u1 < 0.55) {
      // THE WARM FRONT
      out.high = { genus: 'cs', cover: (0.45 + 0.55 * s) * dry };
      if (s < 0.4) out.mid = { genus: u3 > 0.5 ? 'ac-undulatus' : 'ac', cover: (0.28 + 0.5 * s) * dry };
      else out.mid = { genus: 'as', cover: lerp(0.7, 1, sstep(0.4, 0.75, s)) * dry };
      if (s > 0.8) out.low = { genus: 'st', cover: sstep(0.8, 1, s) * dry };
    } else {
      // THE DRY OVERCAST
      out.low = { genus: u3 > 0.6 ? 'sc-undulatus' : 'sc', cover: lerp(0.55, 1, sstep(0, 0.9, s)) * dry };
      out.high = { genus: 'ci', cover: 0.22 * dry };
      if (u4 > 0.5 && s < 0.5) out.mid = { genus: 'ac', cover: 0.2 * (1 - s / 0.5) * dry };
    }
  }
  if (wet > 0) {
    const storm = kind === 'rain' ? sstep(0.8, 1, precip) : 0;
    // The deck arrives over whatever low sheet the dry sky had: its cover takes over as the rain sets in.
    if (wet >= 0.5 || out.low.genus === 'none') out.low = { genus: 'ns', cover: Math.max(wet, out.low.cover * dry), storm };
  }
  // an étage with no cover is ABSENT, by name too (under rain the dry sky's genera came back as { genus: 'ci', cover: 0 } —
  // a consumer deciding which mesh layers to drop by genus would have read a cirrus that is not there)
  if (!(out.high.cover > 0)) out.high = { genus: 'none', cover: 0 };
  if (!(out.mid.cover > 0)) out.mid = { genus: 'none', cover: 0 };
  if (!(out.low.cover > 0)) out.low = { genus: 'none', cover: 0 };
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// 3. The genus tables (numbers only — every transition is a lerp of these)
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════

/** A cumuliform / stratiform layer (the low and middle étages): a height field over the `cells` texture. */
export interface CellParams {
  /** Base altitude and thickness (m). */
  baseM: number; thickM: number;
  /** Vertical optical depth of a full-thickness column. */
  tau: number;
  /** Channel weights of the thickness pattern (pads, waves, fBm — sum 1) and the large-scale mottling's amplitude. */
  wPads: number; wWaves: number; wFbm: number; wLow: number;
  /** The uniform share of the slab (a stratiform layer is mostly `flat`), the dome's exponent, edge erosion, patchiness. */
  flat: number; gamma: number; detail: number; patch: number;
  /** OVER-cover (0..1): how far past a closed sky the deck is — every column starts this share up the dome, so a full deck is mostly thick with a few thin places and no plateau edge. */
  over: number;
  /** Forward-lobe gain (the silver lining) and the lit-side boost toward the antisolar horizon. */
  fwd: number; side: number;
}
/** The low étage's pattern scales (m per tile): pads ≈ 940 m (16 a tile), mottling ≈ 11 km, erosion ≈ 270 m. */
export const LOW_SCALES = { cellsM: sheetScale(15_000), lowM: sheetScale(46_000), detailM: sheetScale(3_200), warpM: 260 } as const;
/** The middle étage's: cloudlets ≈ 336 m (5.5° overhead at 3.5 km — WMO's 1–5° element with its lane), mottling ≈ 8 km. */
export const MID_SCALES = { cellsM: sheetScale(5_376), lowM: sheetScale(32_000), detailM: sheetScale(1_400), warpM: 95 } as const;
export const LOW_GENUS: Record<Exclude<LowGenus, 'none'>, CellParams> = {
  // pads with seams: thin edges bright, bases grey; broken decks open into lanes (patch). gamma 0.4: STEEP SIDES, A FLAT
  // TOP — an element under its inversion is a lump, not a lens; a cone (0.85) let every slanted ray pass over its
  // shoulders, and a half-covered deck closed only +0.05 by 60° (measured on the mirror: +0.11 now, +0.20 by 75°)
  'sc': { baseM: 1150, thickM: 320, tau: 48, wPads: 0.6, wWaves: 0, wFbm: 0.4, wLow: 0.2, flat: 0, gamma: 0.4, detail: 0.16, patch: 0.4, over: 0, fwd: 0.8, side: 0.3 },
  // rolls across the wind (undulatus)
  'sc-undulatus': { baseM: 1150, thickM: 300, tau: 48, wPads: 0.3, wWaves: 0.48, wFbm: 0.22, wLow: 0.18, flat: 0, gamma: 0.45, detail: 0.14, patch: 0.3, over: 0, fwd: 0.8, side: 0.3 },
  // featureless: nine tenths of the slab is uniform, the rest the faintest mottling
  'st': { baseM: 650, thickM: 300, tau: 40, wPads: 0.1, wWaves: 0, wFbm: 0.9, wLow: 0.25, flat: 0.88, gamma: 1, detail: 0.05, patch: 0, over: 0, fwd: 0.6, side: 0 },
  // dark, low, diffuse: vague sagging patches a few km across, no edge anywhere
  'ns': { baseM: 800, thickM: 600, tau: TAU_OVERCAST, wPads: 0.3, wWaves: 0, wFbm: 0.7, wLow: 0.9, flat: 0.4, gamma: 1, detail: 0.1, patch: 0, over: 0.3, fwd: 0.4, side: 0 },
};
export const MID_GENUS: Record<Exclude<MidGenus, 'none'>, CellParams> = {
  // rounded cloudlets, each shaded (grey centre, light edge), in a sheet with lanes and clear patches
  'ac': { baseM: 3500, thickM: 160, tau: 15, wPads: 0.74, wWaves: 0, wFbm: 0.26, wLow: 0.08, flat: 0, gamma: 0.8, detail: 0.1, patch: 0.7, over: 0, fwd: 0.9, side: 0.25 },
  // rows (undulatus): the cloudlets line up across the wind
  'ac-undulatus': { baseM: 3500, thickM: 150, tau: 15, wPads: 0.4, wWaves: 0.42, wFbm: 0.18, wLow: 0.08, flat: 0, gamma: 0.8, detail: 0.1, patch: 0.5, over: 0, fwd: 0.9, side: 0.25 },
  // a grey-blue veil: the sun as through ground glass, no halo
  'as': { baseM: 3500, thickM: 520, tau: 4.5, wPads: 0, wWaves: 0.12, wFbm: 0.88, wLow: 0.3, flat: 0.8, gamma: 1, detail: 0.04, patch: 0, over: 0, fwd: 1.25, side: 0 },
};

/** The high étage: ice — painted filaments and hooks, a veil, grains. */
export interface FibreParams {
  baseM: number; thickM: number;
  /** Strand weights (filaments, hooks), the soft-streak share, the veil 0..1. */
  wFil: number; wHook: number; wStreak: number; veil: number;
  /** Optical depths of a strand core, the full veil, a grain; the halo's strength. */
  tauCi: number; tauCs: number; tauCc: number; halo: number;
  /** The share of the sky the cirrus patches may occupy; the cirrocumulus weight, its ripple share and its patch cover. */
  patchCover: number; ccW: number; ripple: number; ccCover: number;
  fwd: number;
}
/** Filament tile 40 × 10 km (a 512² texel is 79 × 20 m), patches ≈ 27 km, grains 140 m (0.95° overhead — WMO's < 1°). */
export const HIGH_SCALES = { alongM: sheetScale(40_000), acrossM: sheetScale(10_000), lowM: sheetScale(107_000), ccM: sheetScale(2_240), warpM: 1500 } as const;
export const HIGH_BASE_M = 8500;
/** The share of a cirrocumulus patch its grains fill (the rest is the blue between them). */
export const CC_GRAIN_COVER = 0.62;
/** The high étage's parameters for a genus at a cover (pure). */
export function highParams(genus: HighGenus, cover: number, seed = 0): FibreParams {
  const c = c01(cover);
  const base = { baseM: HIGH_BASE_M, thickM: 600, fwd: 1 };
  switch (genus) {
    case 'ci-uncinus': return { ...base, wFil: 0.3, wHook: 1, wStreak: 0.12 + 0.3 * c, veil: 0.12 * Math.max(0, c - 0.45), tauCi: 2.4, tauCs: 0.7, tauCc: 0, halo: 0.1, patchCover: Math.min(1, 0.2 + 1.15 * c), ccW: 0, ripple: 0, ccCover: 0 };
    case 'cc': return { ...base, thickM: 300, wFil: 0.22, wHook: 0, wStreak: 0.1, veil: 0, tauCi: 1.4, tauCs: 0.6, tauCc: 1.1, halo: 0, patchCover: Math.min(1, 0.1 + 0.6 * c), ccW: 1, ripple: 0.25 + 0.5 * sheetHash(seed, 7), ccCover: Math.min(1, 0.12 + 1.1 * c) };
    case 'cs': return { ...base, thickM: 900, wFil: 0.18, wHook: 0.05, wStreak: 0.5, veil: 0.3 + 0.7 * c, tauCi: 0.5, tauCs: 0.4 + 0.75 * c, tauCc: 0, halo: 1, patchCover: Math.min(1, 0.45 + 0.8 * c), ccW: 0, ripple: 0, ccCover: 0 };
    case 'ci':
    default: return { ...base, wFil: 1, wHook: 0.22, wStreak: 0.25 + 0.5 * c, veil: 0.14 * Math.max(0, c - 0.4), tauCi: 2.2, tauCs: 0.7, tauCc: 0, halo: 0.12, patchCover: Math.min(1, 0.16 + 1.15 * c), ccW: 0, ripple: 0, ccCover: 0 };
  }
}
/** A low genus' parameters at a storm 0..1: the deck lowers 250 m and its sagging patches deepen. */
export function lowParams(genus: Exclude<LowGenus, 'none'>, storm = 0): CellParams {
  const p = { ...LOW_GENUS[genus] };
  if (genus === 'ns' && storm > 0) { const s = c01(storm); p.baseM -= 250 * s; p.flat = lerp(p.flat, 0.28, s); p.over = lerp(p.over, 0.5, s); p.wLow = lerp(p.wLow, 1.1, s); p.wPads = lerp(p.wPads, 0.42, s); p.wFbm = 1 - p.wPads; }
  return p;
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// 4. The noise (pure, deterministic): two tiling RGBA8 textures
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════

export interface SheetNoise {
  size: number;
  /** R pads (weighted Voronoi cloudlets) · G fBm · B billow detail · A waves — each EQUALISED (uniform on 0..1). */
  cells: Uint8Array;
  /** R filaments · G hooks (uncinus) — raw densities; B soft streaks · A fine combed grain — equalised. */
  fibres: Uint8Array;
  /** Four INDEPENDENT smooth fBm fields, equalised (256²): the warp (r, g), the patch field (b), the large mottling (a). */
  smooth: Uint8Array; smoothSize: number;
  /** Mean of the two raw strand channels (0..1) — the far field's density. */
  meanFil: number; meanHook: number;
}

function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
/** Periodic gradient noise (periods ≤ 256), −1..1-ish. */
function makePerlin(seed: number): (x: number, y: number, px: number, py: number) => number {
  const r = rng(seed), perm = new Uint8Array(512), gx = new Float32Array(256), gy = new Float32Array(256);
  const p = Array.from({ length: 256 }, (_, i) => i);
  for (let i = 255; i > 0; i--) { const j = Math.floor(r() * (i + 1)); const t = p[i]; p[i] = p[j]; p[j] = t; }
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255];
  for (let i = 0; i < 256; i++) { const a = r() * Math.PI * 2; gx[i] = Math.cos(a); gy[i] = Math.sin(a); }
  const fade = (t: number): number => t * t * t * (t * (t * 6 - 15) + 10);
  return (x, y, px, py) => {
    const x0 = Math.floor(x), y0 = Math.floor(y), fx = x - x0, fy = y - y0;
    const ix0 = ((x0 % px) + px) % px, iy0 = ((y0 % py) + py) % py, ix1 = (ix0 + 1) % px, iy1 = (iy0 + 1) % py;
    const h00 = perm[perm[ix0 & 255] + (iy0 & 255)], h10 = perm[perm[ix1 & 255] + (iy0 & 255)];
    const h01 = perm[perm[ix0 & 255] + (iy1 & 255)], h11 = perm[perm[ix1 & 255] + (iy1 & 255)];
    const d00 = gx[h00] * fx + gy[h00] * fy, d10 = gx[h10] * (fx - 1) + gy[h10] * fy;
    const d01 = gx[h01] * fx + gy[h01] * (fy - 1), d11 = gx[h11] * (fx - 1) + gy[h11] * (fy - 1);
    const u = fade(fx), v = fade(fy);
    return (d00 + (d10 - d00) * u + (d01 + (d11 - d01) * u - (d00 + (d10 - d00) * u)) * v) * 1.41;
  };
}
/** Histogram equalisation: the output is uniform on 0..255 and monotone in the input (level sets keep their shape). */
function equalise(src: Float32Array): Uint8Array {
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < src.length; i++) { const v = src[i]; if (v < lo) lo = v; if (v > hi) hi = v; }
  const BINS = 8192, hist = new Uint32Array(BINS), k = (BINS - 1) / Math.max(1e-12, hi - lo);
  for (let i = 0; i < src.length; i++) hist[Math.floor((src[i] - lo) * k)]++;
  const cdf = new Float32Array(BINS); let acc = 0;
  for (let b = 0; b < BINS; b++) { acc += hist[b]; cdf[b] = (acc - hist[b] * 0.5) / src.length; }
  const out = new Uint8Array(src.length);
  for (let i = 0; i < src.length; i++) out[i] = Math.max(0, Math.min(255, Math.round(cdf[Math.floor((src[i] - lo) * k)] * 255)));
  return out;
}

let noiseCache: SheetNoise | null = null;
/** Bake the two textures (≈ 200 ms once; cached). */
export function bakeSheetNoise(size = SHEET_TEX, seed = 20261007): SheetNoise {
  if (noiseCache && noiseCache.size === size) return noiseCache;
  const n2 = size * size;
  const pA = makePerlin(seed + 1), pB = makePerlin(seed + 2), pC = makePerlin(seed + 3), pD = makePerlin(seed + 4);
  const fbm = (p: typeof pA, x: number, y: number, fx: number, fy: number, oct: number): number => {
    let s = 0, a = 0.5, f = 1;
    for (let o = 0; o < oct; o++) { s += a * p(x * fx * f, y * fy * f, fx * f, fy * f); a *= 0.5; f *= 2; }
    return s;
  };
  // ── cells ──
  const pads = new Float32Array(n2), fb = new Float32Array(n2), det = new Float32Array(n2), wav = new Float32Array(n2);
  const C = SHEET_CELLS, hr = rng(seed + 11);
  const jit = new Float32Array(C * C * 3);
  for (let i = 0; i < jit.length; i++) jit[i] = hr();
  for (let j = 0; j < size; j++) {
    const y = (j + 0.5) / size;
    for (let i = 0; i < size; i++) {
      const x = (i + 0.5) / size, o = j * size + i;
      // pads: a size-weighted Voronoi over a jittered lattice, the query point warped so no pad is a circle
      const wu = x * C + 0.5 * fbm(pA, x, y, 6, 6, 3), wv = y * C + 0.5 * fbm(pB, x, y, 6, 6, 3);
      const cx = Math.floor(wu), cy = Math.floor(wv);
      let d1 = 9, d2 = 9;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const gx = cx + dx, gy = cy + dy, k = ((((gy % C) + C) % C) * C + (((gx % C) + C) % C)) * 3;
        const px = gx + 0.5 + (jit[k] - 0.5) * 0.8, py = gy + 0.5 + (jit[k + 1] - 0.5) * 0.8;
        const d = Math.hypot(wu - px, wv - py) / (0.62 + 0.8 * jit[k + 2] * jit[k + 2]);
        if (d < d1) { d2 = d1; d1 = d; } else if (d < d2) d2 = d;
      }
      // lumps on the pads: a finer lattice (3 a cell) swells and pinches the dome — the cauliflower of a real element
      const lu = wu * 3 + 5.3, lv = wv * 3 + 2.1, lx = Math.floor(lu), ly = Math.floor(lv), C3 = C * 3;
      let l1 = 9;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const gx = lx + dx, gy = ly + dy, hx = ((gx % C3) + C3) % C3, hy = ((gy % C3) + C3) % C3;
        const a = sheetHash(hx * 131 + hy, 21), b2 = sheetHash(hx * 131 + hy, 22);
        const d = Math.hypot(lu - (gx + 0.5 + (a - 0.5) * 0.8), lv - (gy + 0.5 + (b2 - 0.5) * 0.8));
        if (d < l1) l1 = d;
      }
      pads[o] = (0.8 * (1 - d1) + 0.2 * (d2 - d1)) * (0.7 + 0.6 * (0.5 + fbm(pC, x, y, 5, 5, 3))) * (0.8 + 0.4 * (1 - l1));
      fb[o] = fbm(pD, x, y, 4, 4, 5);
      // billow: |noise| folded — the cauliflower erosion of an edge
      let b = 0, a = 0.5, f = 1;
      for (let oc = 0; oc < 4; oc++) { b += a * (1 - 2 * Math.abs(pB(x * 12 * f + 7.3, y * 12 * f + 1.9, 12 * f, 12 * f))); a *= 0.5; f *= 2; }
      det[o] = b;
      // waves: crests across u, their phase wandering, their amplitude broken along the crest
      const ph = 12 * x + 0.9 * fbm(pC, x + 0.31, y + 0.57, 3, 3, 3) + 0.25 * fbm(pA, x, y, 2, 9, 2);
      wav[o] = (0.5 + 0.5 * Math.sin(2 * Math.PI * ph)) * (0.72 + 0.6 * (0.5 + fbm(pD, x + 0.13, y + 0.71, 2, 6, 2)));
    }
  }
  const cells = new Uint8Array(n2 * 4);
  const ch = [equalise(pads), equalise(fb), equalise(det), equalise(wav)];
  for (let i = 0; i < n2; i++) { cells[i * 4] = ch[0][i]; cells[i * 4 + 1] = ch[1][i]; cells[i * 4 + 2] = ch[2][i]; cells[i * 4 + 3] = ch[3][i]; }

  // ── fibres ── (u = along the wind; a texel is 4× longer along than across, so radii are given in metres)
  const mU = HIGH_SCALES.alongM / size, mV = HIGH_SCALES.acrossM / size;
  const fil = new Float32Array(n2), hook = new Float32Array(n2);
  const wrap = (v: number): number => ((v % size) + size) % size;
  /** One gaussian column (across the wind) of a strand at a texel column. */
  const column = (acc: Float32Array, uPx: number, vPx: number, sig: number, w: number): void => {
    const iu = wrap(Math.round(uPx)), r3 = Math.ceil(sig * 3), v0 = Math.floor(vPx);
    for (let dv = -r3; dv <= r3 + 1; dv++) { const dd = v0 + dv - vPx; acc[wrap(v0 + dv) * size + iu] += w * Math.exp((-dd * dd) / (2 * sig * sig)); }
  };
  const r = rng(seed + 21);
  const gauss = (): number => (r() + r() + r() + r() - 2) * 0.87;
  // filaments: PLUMES — a dense head and a fan of fine hairs that leave it, spread, bend together and thin out (the
  // feathers and mares' tails of real cirrus; parallel bundles read as wood grain). Painted in metres, splatted in texels.
  const hair = (acc: Float32Array, x0: number, y0: number, th0: number, kappa: number, lenM: number, w: number, sig0: number, sig1: number): void => {
    let x = x0, y = y0, th = th0;
    const ds = 55, n = Math.max(4, Math.round(lenM / ds));
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      column(acc, x / mU, y / mV, lerp(sig0, sig1, t) / mV, w * Math.min(1, t * 9 + 0.25) * Math.pow(1 - t, 1.25));
      th += kappa * ds * (1 + 0.8 * Math.sin(t * 5 + x0)) + (r() - 0.5) * 0.02;
      x += Math.cos(th) * ds; y += Math.sin(th) * ds;
    }
  };
  for (let b = 0; b < 30; b++) {
    const x0 = r() * HIGH_SCALES.alongM, y0 = r() * HIGH_SCALES.acrossM, th0 = (r() - 0.5) * 0.5;
    const headR = 110 + 300 * r(), spread = 0.12 + 0.34 * r(), kappa = (r() - 0.5) * 2 / 7_000, L = 1800 + 6_500 * r() * r() + 1500 * r(), nH = 10 + Math.floor(r() * 26);
    // the head: a soft dense core, longer along the wind
    const su = (headR * 1.7) / mU, sv = headR / mV, ru = Math.ceil(su * 2.6), rv = Math.ceil(sv * 2.6), hu = Math.round(x0 / mU), hv = Math.round(y0 / mV), hw = 0.3 + 0.5 * r();
    for (let dv = -rv; dv <= rv; dv++) for (let du = -ru; du <= ru; du++) fil[wrap(hv + dv) * size + wrap(hu + du)] += hw * Math.exp(-(du * du) / (2 * su * su) - (dv * dv) / (2 * sv * sv));
    for (let hI = 0; hI < nH; hI++) {
      const oy = gauss() * headR, ox = gauss() * headR * 1.6;
      // a hair leaves on the side of the head it starts on: the fan
      hair(fil, x0 + ox, y0 + oy, th0 + (oy / headR) * spread * 0.7 + gauss() * spread * 0.35, kappa * (0.6 + 0.8 * r()), L * (0.35 + 0.75 * r()), 0.07 + 0.16 * r(), 14 + 20 * r(), 45 + 75 * r());
    }
  }
  // and a few long straight bands (radiatus), each a tight sheaf
  for (let b = 0; b < 4; b++) {
    const x0 = r() * HIGH_SCALES.alongM, y0 = r() * HIGH_SCALES.acrossM, th0 = (r() - 0.5) * 0.06, L = 14_000 + 14_000 * r();
    for (let hI = 0; hI < 9; hI++) hair(fil, x0 + gauss() * 1500, y0 + gauss() * 90, th0 + gauss() * 0.012, 0, L * (0.6 + 0.5 * r()), 0.1 + 0.14 * r(), 18 + 20 * r(), 40 + 50 * r());
  }
  // hooks (uncinus): a dense head and a comb of fall-streaks that leave it at an angle and bend into the shear
  for (let g = 0; g < 15; g++) {
    const ug = r(), vg = r(), side = r() < 0.5 ? -1 : 1, nE = 3 + Math.floor(r() * 5);
    for (let e = 0; e < nE; e++) {
      const u0 = ug + gauss() * 0.07, v0 = vg + gauss() * 0.09, rh = 70 + 110 * r();
      const su = rh / mU, sv = rh / mV, ru = Math.ceil(su * 3), rv = Math.ceil(sv * 3);
      for (let dv = -rv; dv <= rv; dv++) for (let du = -ru; du <= ru; du++)
        hook[wrap(Math.round(v0 * size) + dv) * size + wrap(Math.round(u0 * size) + du)] += 0.45 * Math.exp(-(du * du) / (2 * su * su) - (dv * dv) / (2 * sv * sv));
      const nT = 6 + Math.floor(r() * 8);
      for (let t = 0; t < nT; t++) {
        const Lt = 0.1 + 0.28 * r(), A = (0.014 + 0.05 * r()) * side, off = gauss() * 0.006, sig = 0.6 + 0.8 * r(), w = 0.22 + 0.3 * r();
        const steps = Math.max(8, Math.round(Lt * size));
        for (let x = 0; x <= steps; x++) {
          const s = x / steps;
          column(hook, u0 * size - x, (v0 + off * (1 + 3 * s) + A * (1 - Math.exp(-s / 0.1)) + 0.012 * side * s) * size, sig * (0.9 + 1.4 * s), w * Math.pow(1 - s, 1.2));
        }
      }
    }
  }
  // every hair wears a soft halo of itself (a blurred copy, 3 texels across, 1.5 along): ice falls and diffuses — a
  // filament with a hard edge is a scratch, not a cloud
  const soften = (a: Float32Array): void => {
    const tmp = new Float32Array(n2), kv = [0.06, 0.12, 0.2, 0.24, 0.2, 0.12, 0.06], ku = [0.25, 0.5, 0.25];
    for (let j = 0; j < size; j++) for (let i = 0; i < size; i++) { let v = 0; for (let k = -3; k <= 3; k++) v += kv[k + 3] * a[wrap(j + k * 2) * size + i]; tmp[j * size + i] = v; }
    for (let j = 0; j < size; j++) for (let i = 0; i < size; i++) { let v = 0; for (let k = -1; k <= 1; k++) v += ku[k + 1] * tmp[j * size + wrap(i + k)]; a[j * size + i] = a[j * size + i] * 0.55 + v * 0.9; }
  };
  soften(fil); soften(hook);
  const streak = new Float32Array(n2), fine = new Float32Array(n2);
  for (let j = 0; j < size; j++) for (let i = 0; i < size; i++) {
    const x = (i + 0.5) / size, y = (j + 0.5) / size, o = j * size + i;
    streak[o] = fbm(pA, x, y, 3, 9, 4);
    fine[o] = fbm(pC, x, y, 10, 64, 3);
  }
  const fibres = new Uint8Array(n2 * 4);
  const es = equalise(streak), ef = equalise(fine);
  let mf = 0, mh = 0;
  for (let i = 0; i < n2; i++) {
    const a = 1 - Math.exp(-fil[i] * 1.0), h = 1 - Math.exp(-hook[i] * 1.15);
    mf += a; mh += h;
    fibres[i * 4] = Math.round(a * 255); fibres[i * 4 + 1] = Math.round(h * 255); fibres[i * 4 + 2] = es[i]; fibres[i * 4 + 3] = ef[i];
  }
  // ── smooth ── (a low-frequency read of `cells` gave pads and waves where a smooth field was meant: the warp must be C¹)
  const SS = 256, sn = SS * SS, pE = makePerlin(seed + 5), pF = makePerlin(seed + 6);
  const sm = [new Float32Array(sn), new Float32Array(sn), new Float32Array(sn), new Float32Array(sn)];
  for (let j = 0; j < SS; j++) for (let i = 0; i < SS; i++) {
    const x = (i + 0.5) / SS, y = (j + 0.5) / SS, o = j * SS + i;
    sm[0][o] = fbm(pE, x, y, 3, 3, 3); sm[1][o] = fbm(pF, x, y, 3, 3, 3);
    sm[2][o] = fbm(pE, x + 0.37, y + 0.61, 4, 4, 4); sm[3][o] = fbm(pF, x + 0.19, y + 0.83, 4, 4, 4);
  }
  const smooth = new Uint8Array(sn * 4), se = sm.map(equalise);
  for (let i = 0; i < sn; i++) { smooth[i * 4] = se[0][i]; smooth[i * 4 + 1] = se[1][i]; smooth[i * 4 + 2] = se[2][i]; smooth[i * 4 + 3] = se[3][i]; }
  noiseCache = { size, cells, fibres, smooth, smoothSize: SS, meanFil: mf / n2, meanHook: mh / n2 };
  return noiseCache;
}

/** Bilinear CPU sample of one channel (wrapping) — the mirror's sampler. */
export function sampleNoise(data: Uint8Array, size: number, ch: number, u: number, v: number): number {
  const x = (u - Math.floor(u)) * size - 0.5, y = (v - Math.floor(v)) * size - 0.5;
  const x0 = Math.floor(x), y0 = Math.floor(y), fx = x - x0, fy = y - y0;
  const w = (i: number): number => ((i % size) + size) % size;
  const a = data[(w(y0) * size + w(x0)) * 4 + ch], b = data[(w(y0) * size + w(x0 + 1)) * 4 + ch];
  const c = data[(w(y0 + 1) * size + w(x0)) * 4 + ch], d = data[(w(y0 + 1) * size + w(x0 + 1)) * 4 + ch];
  return (a + (b - a) * fx + (c + (d - c) * fx - (a + (b - a) * fx)) * fy) / 255;
}

/** Wrap the baked bytes in textures. `T` is the `three/webgpu` namespace (the caller's — this file stays loadable in Node). */
export function createSheetTextures(T: { DataTexture: new (d: Uint8Array, w: number, h: number, f: number, t: number) => Texture; RGBAFormat: number; UnsignedByteType: number; RepeatWrapping: number; LinearFilter: number; LinearMipmapLinearFilter: number }, noise: SheetNoise = bakeSheetNoise()): SheetTextures {
  const mk = (d: Uint8Array, size = noise.size): Texture => {
    const t = new T.DataTexture(d, size, size, T.RGBAFormat, T.UnsignedByteType) as Texture & Record<string, unknown>;
    t.wrapS = T.RepeatWrapping as never; t.wrapT = T.RepeatWrapping as never;
    t.magFilter = T.LinearFilter as never; t.minFilter = T.LinearMipmapLinearFilter as never;
    t.generateMipmaps = true; t.anisotropy = 8; t.flipY = false; t.needsUpdate = true;
    return t;
  };
  return { cells: mk(noise.cells), fibres: mk(noise.fibres), smooth: mk(noise.smooth, noise.smoothSize) };
}
export interface SheetTextures { cells: Texture; fibres: Texture; smooth: Texture }

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// 5. Derived numbers (pure): thresholds, the far field's mean, the pattern's mean brightness
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════

/** Samples of six independent uniforms (a Halton set, bases 2…13): the six equalised fields a cell layer composes. */
const CAL_N = 1024;
const calU: Float32Array = (() => {
  const u = new Float32Array(CAL_N * 6), bases = [2, 3, 5, 7, 11, 13];
  for (let i = 0; i < CAL_N; i++) for (let d = 0; d < 6; d++) { let f = 1, r = 0, n = i + 1; const b = bases[d]; while (n > 0) { f /= b; r += f * (n % b); n = Math.floor(n / b); } u[i * 6 + d] = r; }
  return u;
})();

/** What the cell shader needs beside the genus' own numbers. */
export interface CellDerived {
  /** The pattern threshold at the cover, its shift per unit of the patch field (± 0.5), the span to full thickness. */
  thr: number; thrPatch: number; span: number;
  /** The field's mean thickness (the far field), and the mean of B over the pattern at `tauEff`. */
  farHgt: number; meanB: number;
  /** The optical depth after THE DECK THICKENS WITH THE LIGHT MODEL. */
  tauEff: number;
  /** THE FLAT FLOOR HAS AN EDGE: the shift (in spans) that closes the floor's edge as the cover closes (flatEdgeShift). */
  edge: number;
}
/**
 * THE FLAT FLOOR HAS AN EDGE (FIX 2026-10-07, the coordinator's whole-sky bed): a slab genus (stratus 0.88, altostratus
 * 0.8, nimbostratus 0.4 of its thickness as a uniform floor) kept that floor where its pattern said NO CLOUD, so its
 * cover gated nothing — a stratus arriving at cover 0.04 under a warm front's altostratus was a full τ-35 sheet in the
 * altostratus' shade (the whole sky dark slate-blue between weather covers 0.87 and 0.93), and alone a white lid over
 * a clear day. The floor now exists only where the pattern covers: it rises over FLAT_EDGE_W of the span past the
 * calibrated threshold, and the edge moves out of the pattern as the cover closes 0.85 → 1, so a closed deck is the
 * slab it was, bit for bit. A field genus (flat 0) is untouched. (0.12, not wider: the slab is marched in 2–6 segments,
 * and a floor that rises slowly draws one terrace per segment round every gap — pictured at 0.35.)
 */
export const FLAT_EDGE_W = 0.12;
export const flatEdgeShift = (cover: number): number => FLAT_EDGE_W * sstep(0.85, 1, c01(cover));
/** The thickness of a column whose pattern value is `pat` (the shader's own arithmetic). */
export function cellThickness(p: CellParams, d: Pick<CellDerived, 'thr' | 'span'> & { edge?: number }, pat: number, thrShift = 0): number {
  const x = c01((pat - (d.thr + thrShift)) / d.span);
  // the floor's edge (a caller without `edge` gets the closed slab — the old arithmetic)
  const e = p.flat > 0 ? sstep(0, FLAT_EDGE_W, x + (d.edge ?? FLAT_EDGE_W)) : 0;
  return p.flat * e + (1 - p.flat) * Math.pow(x, p.gamma);
}
const cellMemo = new Map<string, CellDerived>();
/**
 * Thresholds and means for a cell layer at a cover under a cloudiness (0..1 — photometry's). `deck` is the étage's
 * EFFECTIVE closing weight — nothing under it × deckClosure(cover), as SheetRuntime.drive computes it (CellDrive.deck);
 * the default 0 leaves the genus' own depth (a direct caller never gets an un-closed thickening by accident).
 *
 * THE COVER IS CALIBRATED ON WHAT THE SHADER COMPOSES (FIX 2026-10-07, verify:cloud-sheets' first run): the pattern is
 * three weighted fields PLUS the edge erosion, the large mottling and the patch field's shift of the threshold — six
 * equalised uniforms. Solving the threshold on the first three alone left the other three to widen the distribution:
 * a sheet asked for 0.9 covered 0.70 (altocumulus, whose patch field swings the local cover hardest) and one asked for
 * 0.3 covered 0.34 — the drawn sky and the light model's cloudiness disagreed. The threshold is now the one at which
 * the COMPOSED field covers what was asked (a bisection over a fixed 1,024-point Halton set; memoised).
 */
export function deriveCell(p: CellParams, cover: number, cloudiness = 0, deck = 0): CellDerived {
  const c = c01(cover), cw = c01(cloudiness) * c01(deck);
  const key = `${p.wPads.toFixed(3)},${p.wWaves.toFixed(3)},${p.wFbm.toFixed(3)},${p.wLow.toFixed(3)},${p.detail.toFixed(3)},${p.patch.toFixed(3)},${p.flat.toFixed(3)},${p.gamma.toFixed(3)},${p.over.toFixed(3)},${p.tau.toFixed(2)},${c.toFixed(4)},${cw.toFixed(4)}`;
  const hit = cellMemo.get(key);
  if (hit) return hit;
  // the composed pattern value of each sample (no patch shift), and its patch coordinate
  const v = new Float32Array(CAL_N), pc = new Float32Array(CAL_N);
  for (let i = 0; i < CAL_N; i++) {
    const o = i * 6;
    v[i] = p.wPads * calU[o] + p.wWaves * calU[o + 1] + p.wFbm * calU[o + 2] + p.detail * (calU[o + 3] - 0.5) + p.wLow * (calU[o + 4] - 0.5);
    pc[i] = calU[o + 5] - 0.5;
  }
  const sorted = Float32Array.from(v).sort();
  const qv = (q: number): number => sorted[Math.max(0, Math.min(CAL_N - 1, Math.round(c01(q) * (CAL_N - 1))))];
  // the patch field moves the LOCAL cover by about ± patch: the threshold's slope at this cover × 2·patch (per unit of the field)
  const dc = Math.max(0.02, Math.min(0.2, p.patch));
  const thrPatch = p.patch > 0 ? ((qv(1 - c01(c + dc)) - qv(1 - c01(c - dc))) / (2 * dc)) * 2 * p.patch : 0;
  // the shader covers where  v > thr − patchCoord·thrPatch : solve thr for the asked cover
  const w = new Float32Array(CAL_N);
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < CAL_N; i++) { w[i] = v[i] + pc[i] * thrPatch; if (w[i] < lo) lo = w[i]; if (w[i] > hi) hi = w[i]; }
  const frac = (t: number): number => { let n = 0; for (let i = 0; i < CAL_N; i++) if (w[i] > t) n++; return n / CAL_N; };
  let a = lo - 1e-3, b = hi + 1e-3;
  for (let it = 0; it < 24; it++) { const m = (a + b) / 2; if (frac(m) > c) a = m; else b = m; }
  const thr0 = c >= 1 ? lo - 1e-3 : c <= 0 ? hi + 1e-3 : (a + b) / 2;
  const span = Math.max(0.1, qv(0.985) - thr0);
  const thr = thr0 - c01(p.over) * span * c;
  // THE DECK THICKENS WITH THE LIGHT MODEL — geometrically, late (c³): a veil at cloudiness 0.5 still passes its
  // watery sun (a linear ramp had an altostratus at τ 70 by then — a dark lid), and a closed sky is TAU_OVERCAST.
  const tauEff = p.tau * Math.pow(Math.max(1, TAU_OVERCAST / p.tau), cw * cw * cw);
  let h = 0, bm = 0;
  const edge = flatEdgeShift(c);
  for (let i = 0; i < CAL_N; i++) { const t = cellThickness(p, { thr, span, edge }, v[i], -pc[i] * thrPatch); h += t; bm += deckBrightness(tauEff * t); }
  const out: CellDerived = { thr, thrPatch, span, farHgt: h / CAL_N, meanB: bm / CAL_N, tauEff, edge };
  if (cellMemo.size > 256) cellMemo.clear();
  cellMemo.set(key, out);
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// 6. The light (pure): what each étage is lit by, from the frame's own numbers
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════

export interface SheetLightFrame {
  /** Scene units per lux (the exposure curve's pre-exposure). */
  k: number;
  sinEl: number; sunDir: V3;
  /** Transmittance toward the sun from each étage's altitude and from the ground (RGB, 0..1). */
  tSun: Record<Etage, V3>; tGround: V3;
  /** photometry.lightModel's clear global and overcast lux, the cloudiness 0..1, the dust's dimming. */
  clearGlobalLux: number; overcastLux: number; cloudiness: number; sunDim: number;
  /** The sky's mean radiance (any scale — only its hue is used). */
  skyHue: V3;
  /**
   * The moon: direction, elevation sine, key (normal lux, max-normalised colour, as photometry.moonModel), its sky's lux
   * and that sky's OWN colour (MoonModel.diffuseColor — never the sun-sky's hue: at night that is black), and the deck's
   * direct transmittance its key carries.
   */
  moon: null | { dir: V3; sinEl: number; keyLux: number; keyColor: readonly [number, number, number]; diffuseLux: number; diffuseColor: readonly [number, number, number]; tCloud: number };
  /** The DRAWN night deck at the zenith (scene units): sky_sampler.nightSkyRadiance at cloudiness 1 × luxToScene × the night factor. */
  nightDeck: V3;
  /** The rain dome's mix 0..1 (the night shape follows the dome's law under it). */
  visMix: number;
}
export interface EtageLight {
  /** Unit direction to the key (the sun, or the moon once it outshines the sun at this étage). */
  keyDir: V3; moonWeight: number;
  /** The key's NORMAL illuminance at the étage, scene units RGB (the forward lobes and the halo). */
  sunL: V3;
  /** The overcast-law zenith radiance of the light on the étage's top, scene units RGB (× B(τ) × (1 + 2 sin θ)/3 = a base). */
  eDiff: V3;
  /** The night deck this étage shows (scene units RGB) and its shape a + b·sin θ. */
  night: V3; nightA: number; nightB: number;
}
/** sin of the horizon's dip at an altitude (m): how far below the ground's horizon a layer still sees the sun. */
export const horizonDip = (altM: number): number => Math.sin(Math.sqrt((2 * Math.max(0, altM)) / EARTH_RADIUS_M));

function slerp(a: V3, b: V3, t: number): V3 {
  const cosT = Math.min(1, Math.max(-1, a.x * b.x + a.y * b.y + a.z * b.z)), th = Math.acos(cosT);
  let px = b.x - cosT * a.x, py = b.y - cosT * a.y, pz = b.z - cosT * a.z, pl = Math.hypot(px, py, pz);
  if (pl < 1e-6) {
    if (cosT > 0) return { ...a };
    px = -a.y * a.x; py = 1 - a.y * a.y; pz = -a.y * a.z; pl = Math.hypot(px, py, pz); // antipodal: over the zenith side (cloud_light.slerpDir's rule)
    if (pl < 1e-6) { px = 1; py = 0; pz = 0; pl = 1; }
  }
  const s = Math.sin(th * t) / pl, c = Math.cos(th * t);
  return { x: a.x * c + px * s, y: a.y * c + py * s, z: a.z * c + pz * s };
}

export interface SheetLightOptions {
  /** The share of the key the étages ABOVE let through (1 = open). */
  shade?: number;
  /** How much this étage is the sky's closing deck, its own closure included (CellDrive.deck) — the cloudiness acts through it. */
  deck?: number;
  /** The pattern's mean brightness at the thickened depth (deriveCell.meanB). */
  meanB?: number;
  /** The genus' optical depth: a thick layer's BASE takes no grazing light (its sides do — the forward lobes). */
  tau?: number;
  /** How much an étage ABOVE this one is the sky's closing deck (its CellDrive.deck × presence): this one stands in its light. */
  under?: number;
}
/**
 * One étage's light at its base altitude `altM`. Under a closing deck (cloudiness × `deck`) the source becomes the
 * frame's OVERCAST light over the pattern's mean brightness, so the étage's mean luminance IS the overcast: the sun's
 * share is the engine dome's own zenith (photometry's overcastLux — exact), the moon's is its key × the overcast
 * fraction plus its sky (photometry.moonModel's own overcast).
 */
export function sheetLight(f: SheetLightFrame, etage: Etage, altM: number, o: SheetLightOptions = {}): EtageLight {
  const shade = o.shade ?? 1, meanB = o.meanB ?? OVERCAST_GLOBAL_FRACTION;
  const T = f.tSun[etage], k = f.k, dim = Math.max(0, 1 - c01(f.sunDim)) * c01(shade);
  const fade = horizonFade(f.sinEl + horizonDip(altM));
  const sunLum = E0_LUX * lum709(T.x, T.y, T.z) * fade * dim;
  let w = 0;
  const mo = f.moon;
  let moonRgb: V3 = { x: 0, y: 0, z: 0 }, moonLum = 0;
  if (mo && mo.keyLux > 0 && mo.sinEl > -0.02) {
    // the key over the cloud: undo the deck the ground's key carries (cloud_light.cloudLight's rule)
    const ill = (mo.keyLux / Math.max(0.05, mo.tCloud)) * c01(shade);
    moonRgb = { x: ill * mo.keyColor[0], y: ill * mo.keyColor[1], z: ill * mo.keyColor[2] };
    moonLum = lum709(moonRgb.x, moonRgb.y, moonRgb.z);
    const t = c01((Math.log2(Math.max(1e-30, moonLum) / Math.max(1e-30, sunLum)) + 2) / 4);
    w = t * t * (3 - 2 * t);
  }
  const sunRgb: V3 = { x: E0_LUX * T.x * fade * dim, y: E0_LUX * T.y * fade * dim, z: E0_LUX * T.z * fade * dim };
  const keyDir = w > 0 && mo ? slerp(f.sunDir, mo.dir, w) : { ...f.sunDir };
  const sunL: V3 = { x: (sunRgb.x * (1 - w) + moonRgb.x * w) * k, y: (sunRgb.y * (1 - w) + moonRgb.y * w) * k, z: (sunRgb.z * (1 - w) + moonRgb.z * w) * k };
  const c = c01(f.cloudiness) * c01(o.deck ?? 0);
  // a grazing key still lights a THIN layer edge-on (a set sun on cirrus); a thick layer's base sees the sky and the
  // ground, never the low sun — its sides do. (FIX 2026-10-07: the whole share on every genus painted stratocumulus
  // bases tan on every bearing at a 5° sun.)
  const side = SIDE_LIGHT * Math.exp(-Math.max(0, o.tau ?? 0) / 6);
  const up = (sn: number): number => lerp(Math.max(sn, side), Math.max(sn, 0), c);
  // the sun-sky's hue per unit luminance (a neutral blue when that sky is black — its lux is then ~0 too, never 0/ε)
  const hl0 = lum709(f.skyHue.x, f.skyHue.y, f.skyHue.z);
  const sH: V3 = hl0 > 1e-12 ? { x: f.skyHue.x / hl0, y: f.skyHue.y / hl0, z: f.skyHue.z / hl0 } : { x: 0.82, y: 0.96, z: 1.38 };
  const sunSky = Math.max(0, f.clearGlobalLux - E0_LUX * lum709(f.tGround.x, f.tGround.y, f.tGround.z) * horizonFade(f.sinEl) * Math.max(0, f.sinEl));
  const us = up(f.sinEl), um = mo ? up(mo.sinEl) : 0;
  const q = (9 / (7 * Math.PI)) * k;
  // the light on the top, in two parts: the sun's (its horizontal share + its clear sky) and the moon's (+ its sky)
  const pS: V3 = { x: q * (sunRgb.x * us + sunSky * sH.x), y: q * (sunRgb.y * us + sunSky * sH.y), z: q * (sunRgb.z * us + sunSky * sH.z) };
  // UNDER A CLOSING DECK (FIX 2026-10-07, the coordinator's whole-sky bed): an étage that stands BELOW the sky's deck
  // (the first stratus under a warm front's altostratus) is lit from above by that deck — the frame's overcast light,
  // grey — never by the clear sky's blue diffuse: its pieces drew slate-BLUE under a grey sky. (`under` 0 — no deck
  // above, every caller before — leaves the light as it was.)
  const ua = c01(f.cloudiness) * c01(o.under ?? 0);
  if (ua > 0) {
    const hl = lum709(DECK_HUE[0], DECK_HUE[1], DECK_HUE[2]), ol = (q * Math.max(0, f.overcastLux)) / hl;
    pS.x = lerp(pS.x, ol * DECK_HUE[0], ua); pS.y = lerp(pS.y, ol * DECK_HUE[1], ua); pS.z = lerp(pS.z, ol * DECK_HUE[2], ua);
  }
  // THE MOON'S SKY IS THE MOON'S (FIX 2026-10-07, found by verify:cloud-sheets on its first run): its lux rode the
  // SUN-sky's hue ÷ that hue's luminance — and at night the sampler's sun-sky is exactly (0, 0, 0), so the term was 0/ε:
  // every étage lost the moonlit sky's light (two thirds of a closed deck's moonlight: 4.16·10⁻³ of the moon model's
  // 1.29·10⁻² scene units) and what was left was the key's orange alone — brown clouds under a full moon.
  const moonSky = mo ? Math.max(0, mo.diffuseLux) : 0;
  const mc = mo ? mo.diffuseColor : [0, 0, 0], ml = Math.max(1e-9, lum709(mc[0], mc[1], mc[2]));
  const mH: V3 = { x: mc[0] / ml, y: mc[1] / ml, z: mc[2] / ml };
  const pM: V3 = { x: q * (moonRgb.x * um + moonSky * mH.x), y: q * (moonRgb.y * um + moonSky * mH.y), z: q * (moonRgb.z * um + moonSky * mH.z) };
  // THE DECK THICKENS WITH THE LIGHT MODEL: a closing deck's source is the OVERCAST light over the pattern's mean B.
  // The floor on the mean is half the overcast fraction — a real closed deck never goes under it (TAU_OVERCAST is its
  // thickest), so it only ever bounds a degenerate étage.
  const mb = Math.max(OVERCAST_GLOBAL_FRACTION * 0.5, meanB);
  // the sun's share: the engine dome's own zenith AND its grey, rain or not. (Tried and reverted 2026-10-07: keeping the
  // light's own hue on a DRY closed deck painted a stratus at a 5° sun the colour of milky coffee while the raining
  // nimbostratus beside it — the dome — stayed grey: the sky would change colour the moment rain began. A closed deck is
  // the game's overcast at every hour; a sunset deck's warmth is its thin places and the forward lobes toward the sun.)
  const dz = overcastZenithRadiance(f.overcastLux) * k, vm = c01(f.visMix), dl = lum709(DECK_HUE[0], DECK_HUE[1], DECK_HUE[2]);
  const dS: V3 = { x: (dz * DECK_HUE[0]) / dl / mb, y: (dz * DECK_HUE[1]) / dl / mb, z: (dz * DECK_HUE[2]) / dl / mb };
  // the moon's share (FIX 2026-10-07: the target was the sun's dome alone — zero at night — so a closed deck under a full
  // moon was lit by nothing): its key × the overcast fraction, its sky as the moon model already mixed it
  const mk = q * OVERCAST_GLOBAL_FRACTION * um, ms = q * moonSky;
  const dM: V3 = { x: (moonRgb.x * mk + ms * mH.x) / mb, y: (moonRgb.y * mk + ms * mH.y) / mb, z: (moonRgb.z * mk + ms * mH.z) / mb };
  const eDiff: V3 = { x: lerp(pS.x + pM.x, dS.x + dM.x, c), y: lerp(pS.y + pM.y, dS.y + dM.y, c), z: lerp(pS.z + pM.z, dS.z + dM.z, c) };
  const share = NIGHT_DECK_SHARE[etage];
  return {
    keyDir, moonWeight: w, sunL, eDiff,
    night: { x: f.nightDeck.x * share, y: f.nightDeck.y * share, z: f.nightDeck.z * share },
    // dry: the sky shader's deckShape (0.55 + 0.45 sin θ); under the rain dome: the dome's own (1 + 2 sin θ)/3
    nightA: lerp(0.55, 1 / 3, vm), nightB: lerp(0.45, 2 / 3, vm),
  };
}

/** The mean vertical optical depth of an étage's cover (what it shades the étages below with). */
export function sunShadeThrough(cover: number, tauMean: number, sinKey: number): number {
  return 1 - c01(cover) * (1 - Math.exp(-Math.max(0, tauMean) / Math.max(0.1, sinKey)));
}

// (sheetLights — the three étages together — is below the runtime: it reads a SheetDrive.)

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// 7. The runtime (pure state): smoothed genus parameters, the wrapped origin, the wind frame
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════

/** A wrapped pattern-frame origin: the camera's XZ turned into the wind frame, less the drift, modulo the tile. */
export function sheetOrigin(camX: number, camZ: number, windAngle: number, driftM: number): [number, number] {
  const cw = Math.cos(windAngle), sw = Math.sin(windAngle);
  const m = (v: number): number => ((v % SHEET_TILE_M) + SHEET_TILE_M) % SHEET_TILE_M;
  return [m(camX * cw + camZ * sw - driftM), m(-camX * sw + camZ * cw)];
}
/** The shell constant c = (y − H)(2R + y + H) in km² (negative under the layer) — doubles, on the CPU. */
export function shellC(camY: number, altM: number): number { return ((camY - altM) * (2 * EARTH_RADIUS_M + camY + altM)) / 1e6; }
/** Slant distance (km) to the shell along a ray of elevation sine `dy` — the shader's own arithmetic. */
export function shellDistanceKm(camY: number, altM: number, dy: number): number {
  const c = shellC(camY, altM), b = ((EARTH_RADIUS_M + camY) / 1000) * dy;
  return -c / (b + Math.sqrt(Math.max(1e-9, b * b - c)));
}
/** 1 well under the base, 0 at and above it. */
export function approachFade(camY: number, baseM: number): number { return sstep(0, SHEET_APPROACH_FADE_M, baseM - camY - 30); }

/** How each étage's wind differs from the surface wind: a veer (rad) and a speed (m/s). */
export const ETAGE_WIND = { high: { veer: 0.44, mps: 26 }, mid: { veer: 0.2, mps: 13 }, low: { veer: 0, mps: 7 } } as const;
/** Slab SEGMENTS per étage (a segment costs one fetch, plus one for the base) and the sunward thickness tap, by graphics tier (the tier defaults are untouched — this only reads the name). */
export function sheetQuality(tier: string): { low: number; mid: number; sunward: boolean } {
  switch (tier) {
    case 'ultra': return { low: 6, mid: 5, sunward: true };
    case 'high': return { low: 4, mid: 4, sunward: true };
    case 'med': return { low: 3, mid: 3, sunward: true };
    default: return { low: 2, mid: 2, sunward: false };
  }
}
/**
 * Texture fetches per sky pixel an étage costs when it is ON (the hand-back's table). Counted by hand from the builder —
 * a cell étage: 1 smooth + 1 detail + (segments + 1) cells + the sunward tap; the high étage: smooth + fibres + grain,
 * + 1 cell fetch for cirrocumulus. `verify:cloud-sheets` pins THIS FUNCTION against those literals; nothing counts the
 * fetches the built graph really issues — change the builder, change this by hand.
 */
export function sheetFetches(tier: string, cc = false): Record<Etage, number> {
  const q = sheetQuality(tier), s = q.sunward ? 1 : 0;
  return { high: 3 + (cc ? 1 : 0), mid: q.mid + 3 + s, low: q.low + 3 + s };
}

function lerpNum<T extends object>(a: T, b: T, t: number): T {
  const o = { ...a } as Record<string, number>;
  for (const key of Object.keys(b)) o[key] = lerp((a as Record<string, number>)[key], (b as Record<string, number>)[key], t);
  return o as T;
}

export interface SheetEnv {
  /** The camera's absolute position (m). */
  cam: V3;
  /** The surface wind's direction (unit XZ) — null keeps the last. */
  wind: { x: number; z: number } | null;
  /** photometry's cloudiness and the rain dome's mix. */
  cloudiness: number; visMix: number;
  /** sin of the key's elevation (the sunward tap's reach). */
  keyDir: V3;
}
/** Everything the uniforms take for one cell étage. */
/** `cover` shapes the pattern; `presence` (the approach fade, the rain deck over a higher étage) thins what is drawn; `deck` = how much this étage closes the sky. */
export interface CellDrive { genus: string; cover: number; presence: number; deck: number; p: CellParams; d: CellDerived; c0: number; c1: number; org: [number, number]; cw: number; sw: number; sun: [number, number] }
export interface FibreDrive { genus: string; cover: number; presence: number; p: FibreParams; c0: number; c1: number; org: [number, number]; cw: number; sw: number; tauMean: number }
export interface SheetDrive { rcKm: number; horizonY: number; low: CellDrive; mid: CellDrive; high: FibreDrive }

/**
 * The sheets' state between frames: each étage's parameters eased toward the weather's genus (every transition is a
 * lerp of numbers — the pattern scales are per ÉTAGE, so nothing swims), the origin integrated from the camera's motion
 * and each étage's wind, the wind frame slewed. `place()` is the deterministic form (beds, the lever's pin).
 */
export class SheetRuntime {
  spec: SheetSpec = NO_SHEETS;
  private low = { p: lowParams('sc'), cover: 0, genus: 'none' as string };
  private mid = { p: { ...MID_GENUS.ac }, cover: 0, genus: 'none' as string };
  /** `ref` = the cover the eased parameters stand for (they carry the genus' amounts): the étage's presence is cover / ref. */
  private high = { p: highParams('ci', 0), cover: 0, ref: 0, genus: 'none' as string };
  private org: Record<Etage, [number, number]> = { high: [0, 0], mid: [0, 0], low: [0, 0] };
  private windAngle = 0.3;
  private lastCam: V3 | null = null;
  private seed = 0;

  /** Ease toward a spec over `dt` seconds (`snap` = at once). */
  step(dt: number, spec: SheetSpec, env: SheetEnv, seed = 0, snap = false): void {
    this.spec = spec; this.seed = seed;
    const k = snap ? 1 : 1 - Math.exp(-Math.max(0, Math.min(1, dt)) / 5);
    const ease = <P extends object>(cur: { p: P; cover: number; genus: string }, genus: string, cover: number, target: P | null): void => {
      // an étage that is absent takes its next genus whole; a present one eases every number
      if (target) cur.p = cur.cover < 0.004 || snap ? { ...target } : lerpNum(cur.p, target, k);
      cur.cover = lerp(cur.cover, target ? c01(cover) : 0, k);
      if (cur.cover < 1e-4 && !target) cur.cover = 0;
      cur.genus = target ? genus : cur.cover > 0 ? cur.genus : 'none';
    };
    ease(this.low, spec.low.genus, spec.low.cover, spec.low.genus === 'none' ? null : lowParams(spec.low.genus, spec.low.storm ?? 0));
    ease(this.mid, spec.mid.genus, spec.mid.cover, spec.mid.genus === 'none' ? null : { ...MID_GENUS[spec.mid.genus] });
    const hadHigh = this.high.cover >= 0.004 && !snap;
    ease(this.high, spec.high.genus, spec.high.cover, spec.high.genus === 'none' ? null : highParams(spec.high.genus, spec.high.cover, seed));
    if (spec.high.genus !== 'none') this.high.ref = hadHigh ? lerp(this.high.ref, c01(spec.high.cover), k) : c01(spec.high.cover);
    // the wind frame: slewed (≤ 1.2°/s), turning about the camera
    if (env.wind && Number.isFinite(env.wind.x + env.wind.z) && Math.hypot(env.wind.x, env.wind.z) > 1e-3) {
      const want = Math.atan2(env.wind.z, env.wind.x);
      let d = want - this.windAngle; d = Math.atan2(Math.sin(d), Math.cos(d));
      const lim = snap ? Math.PI : 0.021 * Math.max(0, Math.min(1, dt));
      this.windAngle += Math.max(-lim, Math.min(lim, d));
    }
    const cam = env.cam, last = this.lastCam;
    const dx = last ? cam.x - last.x : 0, dz = last ? cam.z - last.z : 0;
    const t = Math.max(0, Math.min(0.25, dt));
    const wrapT = (v: number): number => ((v % SHEET_TILE_M) + SHEET_TILE_M) % SHEET_TILE_M;
    for (const e of ['high', 'mid', 'low'] as const) {
      const a = this.windAngle + ETAGE_WIND[e].veer, cw = Math.cos(a), sw = Math.sin(a);
      const o = this.org[e];
      // a first frame or a teleport lands anywhere on the tile — the deterministic origin of that spot
      if (!last || Math.hypot(dx, dz) > 5_000) { const p = sheetOrigin(cam.x, cam.z, a, 0); o[0] = p[0]; o[1] = p[1]; }
      else { o[0] = wrapT(o[0] + dx * cw + dz * sw - ETAGE_WIND[e].mps * t); o[1] = wrapT(o[1] - dx * sw + dz * cw); }
    }
    this.lastCam = { x: cam.x, y: cam.y, z: cam.z };
  }

  /** Pin the frame deterministically: the wind's angle and a drift clock (s). */
  place(cam: V3, windAngle: number, timeS: number): void {
    this.windAngle = windAngle;
    for (const e of ['high', 'mid', 'low'] as const) this.org[e] = sheetOrigin(cam.x, cam.z, windAngle + ETAGE_WIND[e].veer, ETAGE_WIND[e].mps * timeS);
    this.lastCam = { x: cam.x, y: cam.y, z: cam.z };
  }

  /** The numbers for this frame's uniforms. */
  drive(env: SheetEnv): SheetDrive {
    const cam = env.cam, y = Number.isFinite(cam.y) ? cam.y : 0;
    const rot = (e: Etage): { cw: number; sw: number } => { const a = this.windAngle + ETAGE_WIND[e].veer; return { cw: Math.cos(a), sw: Math.sin(a) }; };
    const kd = env.keyDir, kh = Math.hypot(kd.x, kd.z), tanEl = Math.max(0.2, Math.max(0.02, kd.y) / Math.max(1e-3, kh));
    // the low étage is the closing deck; the middle one takes that part once nothing is under it
    const lowCover = this.low.cover * approachFade(y, this.low.p.baseM);
    const cell = (e: 'mid' | 'low', s: { p: CellParams; cover: number; genus: string }, lowest: number): CellDrive => {
      const r = rot(e);
      // how much THIS étage is the sky's closing deck: nothing under it, and itself near-full (deckClosure)
      const deck = c01(lowest) * deckClosure(s.cover);
      const d = deriveCell(s.p, s.cover, env.cloudiness, deck);
      // the sunward tap: toward the key by the slab's own shadow length (≤ 2.5 thicknesses), in the pattern frame
      const reach = kh > 1e-3 ? Math.min(2.5, 0.6 / tanEl) * s.p.thickM / kh : 0;
      return {
        genus: s.genus, cover: s.cover, presence: approachFade(y, s.p.baseM) * (e === 'mid' ? 1 - c01(env.visMix) : 1), deck, p: s.p, d,
        c0: shellC(y, s.p.baseM), c1: shellC(y, s.p.baseM + s.p.thickM), org: [this.org[e][0], this.org[e][1]], cw: r.cw, sw: r.sw,
        sun: [(kd.x * r.cw + kd.z * r.sw) * reach, (-kd.x * r.sw + kd.z * r.cw) * reach],
      };
    };
    const low = cell('low', this.low, 1), mid = cell('mid', this.mid, 1 - c01(lowCover * 1.5));
    const hp = this.high.p, hr = rot('high');
    const hPres = (this.high.ref > 1e-4 ? c01(this.high.cover / this.high.ref) : 0) * approachFade(y, hp.baseM) * (1 - c01(env.visMix));
    const n = bakeSheetNoise();
    const tauMean = hp.patchCover * hp.tauCi * (hp.wFil * n.meanFil + hp.wHook * n.meanHook) * 0.9 + hp.tauCs * hp.veil * 0.9 + hp.tauCc * hp.ccCover * 0.4 * hp.ccW;
    const high: FibreDrive = { genus: this.high.genus, cover: this.high.cover, presence: hPres, p: hp, c0: shellC(y, hp.baseM), c1: shellC(y, hp.baseM + hp.thickM), org: [this.org.high[0], this.org.high[1]], cw: hr.cw, sw: hr.sw, tauMean: tauMean * hPres };
    return { rcKm: (EARTH_RADIUS_M + y) / 1000, horizonY: -Math.sqrt((2 * Math.max(0, y)) / EARTH_RADIUS_M), low, mid, high };
  }

  /** The live state (the lever's report). */
  state(): { spec: SheetSpec; shown: Record<Etage, { genus: string; cover: number }>; windAngleDeg: number; seed: number } {
    const r = (v: number): number => +v.toFixed(3);
    return {
      spec: this.spec, seed: this.seed, windAngleDeg: +((this.windAngle * 180) / Math.PI).toFixed(1),
      shown: { high: { genus: this.high.genus, cover: r(this.high.cover) }, mid: { genus: this.mid.genus, cover: r(this.mid.cover) }, low: { genus: this.low.genus, cover: r(this.low.cover) } },
    };
  }
}

/**
 * The three étages' lights for a frame: each lower étage's key is what the ones above let through, the closing deck's
 * source follows the cloudiness (sheetLight).
 */
export function sheetLights(f: SheetLightFrame, d: SheetDrive): Record<Etage, EtageLight> {
  const high = sheetLight(f, 'high', d.high.p.baseM, { tau: d.high.p.tauCi });
  const kY = high.keyDir.y;
  const sMid = sunShadeThrough(1, d.high.tauMean, kY);
  const through = (c: CellDrive): number => { const cov = c01(c.cover) * c.presence; return cov > 1e-3 ? sunShadeThrough(c01(c.cover), (c.d.tauEff * c.d.farHgt) / Math.max(0.05, c01(c.cover)), kY) * c.presence + (1 - c.presence) : 1; };
  const mid = sheetLight(f, 'mid', d.mid.p.baseM, { shade: sMid, deck: d.mid.deck, meanB: d.mid.d.meanB, tau: d.mid.p.tau });
  const low = sheetLight(f, 'low', d.low.p.baseM, { shade: sMid * through(d.mid), deck: d.low.deck, meanB: d.low.d.meanB, tau: d.low.p.tau, under: d.mid.deck * d.mid.presence });
  return { high, mid, low };
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// 8. The CPU mirror of the cell layer (the harness: cover by zenith angle, the far mean)
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════

/** The thickness field of a cell layer at a pattern-frame point (m), as the shader composes it (no warp, sharp). */
export function cellColumnCpu(noise: SheetNoise, p: CellParams, d: CellDerived, sc: { cellsM: number; lowM: number; detailM: number }, u: number, v: number): number {
  const S = noise.size, cd = noise.cells;
  const low = sampleNoise(noise.smooth, noise.smoothSize, 2, u / sc.lowM, v / sc.lowM), lowA = sampleNoise(noise.smooth, noise.smoothSize, 3, u / sc.lowM, v / sc.lowM);
  const det = sampleNoise(cd, S, 2, u / sc.detailM, v / sc.detailM);
  const cu = u / sc.cellsM, cv = v / sc.cellsM;
  const pat = p.wPads * sampleNoise(cd, S, 0, cu, cv) + p.wWaves * sampleNoise(cd, S, 3, cu, cv) + p.wFbm * sampleNoise(cd, S, 1, cu, cv)
    + (det - 0.5) * p.detail + (lowA - 0.5) * p.wLow;
  return cellThickness(p, d, pat, -(low - 0.5) * d.thrPatch);
}
/**
 * The share of rays at a zenith angle (deg) whose slant optical depth through the slab passes `tauMin` — the apparent
 * cover. Flat geometry (the mirror is a statistic, not a picture); the walk is the shader's.
 */
export function apparentCoverCpu(noise: SheetNoise, p: CellParams, cover: number, sc: { cellsM: number; lowM: number; detailM: number }, zenithDeg: number, segments = 4, tauMin = 0.7, rays = 4000): number {
  const d = deriveCell(p, cover), tz = Math.tan((zenithDeg * Math.PI) / 180), cosz = Math.cos((zenithDeg * Math.PI) / 180);
  const r = rng(977);
  let hit = 0;
  for (let i = 0; i < rays; i++) {
    const u0 = r() * SHEET_TILE_M, v0 = r() * SHEET_TILE_M, az = r() * Math.PI * 2, cu = Math.cos(az), cv = Math.sin(az);
    let inside = 0, g0 = 0;
    for (let s = 0; s <= segments; s++) {
      const z = s / segments, off = z * p.thickM * tz;
      const g = cellColumnCpu(noise, p, d, sc, u0 + cu * off, v0 + cv * off) - z;
      if (s > 0) inside += (Math.max(g0, 0) + Math.max(g, 0)) / Math.max(1e-4, Math.abs(g0) + Math.abs(g));
      g0 = g;
    }
    if ((inside / segments) * d.tauEff / cosz > tauMin) hit++;
  }
  return hit / rays;
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// 9. The uniforms
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════

interface LightUniforms { keyDir: N; sunL: N; eDiff: N; night: N; misc: N; hazeK: N; hazeRgb: N; tint: N }
export interface CellSheetUniforms extends LightUniforms { geo: N; org: N; pat: N; thr: N; shp: N; scl: N; opt: N; sun: N; flash: N; flashRgb: N }
export interface FibreSheetUniforms extends LightUniforms { geo: N; org: N; pat: N; tau: N; ptc: N; cc: N; scl: N; opt: N }
export interface CloudSheetUniforms { low: CellSheetUniforms; mid: CellSheetUniforms; high: FibreSheetUniforms }

const lightUniforms = (): LightUniforms => ({
  keyDir: uniform(vec3(0, 1, 0)), sunL: uniform(vec3(0, 0, 0)), eDiff: uniform(vec3(0, 0, 0)), night: uniform(vec3(0, 0, 0)),
  // misc: night shape a, b · the haze's overcast mix · the geometric horizon's sin
  misc: uniform(vec4(0.55, 0.45, 0, 0)), hazeK: uniform(vec3(0, 0, 0)), hazeRgb: uniform(vec3(0, 0, 0)), tint: uniform(vec3(1, 1, 1)),
});
const cellUniforms = (): CellSheetUniforms => ({
  ...lightUniforms(),
  geo: uniform(vec4(-1, -1, 6371, 300)), org: uniform(vec4(0, 0, 1, 0)), pat: uniform(vec4(1, 0, 0, 0)), thr: uniform(vec4(0.5, 0, 0.5, 0.2)),
  shp: uniform(vec4(0, 1, 0, 0)), scl: uniform(vec4(1e-4, 1e-4, 1e-5, 1e-3)), opt: uniform(vec4(1, 0, 1, 0)), sun: uniform(vec4(0, 0, 0, 0)),
  flash: uniform(vec4(0, 0, 1e-6, 0)), flashRgb: uniform(vec3(0.75, 0.84, 1)),
});
/** The three étages' uniforms (one set per graph). */
export function createSheetUniforms(): CloudSheetUniforms {
  return {
    low: cellUniforms(), mid: cellUniforms(),
    high: {
      ...lightUniforms(), geo: uniform(vec4(-1, -1, 6371, 600)), org: uniform(vec4(0, 0, 1, 0)), pat: uniform(vec4(1, 0, 0, 0)), tau: uniform(vec4(1, 1, 1, 0)),
      ptc: uniform(vec4(0.5, 0.07, 0, 0)), cc: uniform(vec4(0, 0, 0.5, 0.5)), scl: uniform(vec4(1e-5, 1e-4, 1e-5, 1e-3)), opt: uniform(vec4(1, 0, 0, 0)),
    },
  };
}

const set3 = (u: N, v: V3): void => { (u.value as { set(x: number, y: number, z: number): void }).set(v.x, v.y, v.z); };
const set4 = (u: N, x: number, y: number, z: number, w: number): void => { (u.value as { set(x: number, y: number, z: number, w: number): void }).set(x, y, z, w); };
const fin = (v: number, d = 0): number => (Number.isFinite(v) ? v : d);

export interface SheetHaze {
  /** Vertical optical depth of the clear air from the camera up to each étage's base (RGB). */
  tauTo: Record<Etage, V3>;
  /** The camera's height (m). */
  camY: number;
}
/** Write one frame's numbers. `lights` from `sheetLight`, `haze` from the sampler, `tint` the droplets' albedo (low / middle). */
export function writeSheetUniforms(u: CloudSheetUniforms, d: SheetDrive, lights: Record<Etage, EtageLight>, haze: SheetHaze, tint: V3 = { x: 1, y: 1, z: 1 }): void {
  // `under` = how much the air below the étage is lit by the étage itself (a closed deck: wholly), `meanB` its mean
  // brightness — the far deck sinks into ITS OWN horizon radiance (source × mean B ÷ 3, the overcast law at the horizon),
  // never into the clear sky's bright ring it hides.
  const light = (lu: LightUniforms, e: Etage, baseM: number, under: number, meanB: number): void => {
    const L = lights[e];
    set3(lu.keyDir, L.keyDir); set3(lu.sunL, L.sunL); set3(lu.eDiff, L.eDiff); set3(lu.night, L.night);
    set4(lu.misc, L.nightA, L.nightB, c01(under), fin(d.horizonY));
    set3(lu.hazeRgb, { x: (L.eDiff.x * meanB + L.night.x) / 3, y: (L.eDiff.y * meanB + L.night.y) / 3, z: (L.eDiff.z * meanB + L.night.z) / 3 });
    // the slant path's optical depth per km of slant distance: the vertical depth over the vertical run
    const run = Math.max(0.05, (baseM - haze.camY) / 1000), t = haze.tauTo[e];
    set3(lu.hazeK, { x: fin(t.x) / run, y: fin(t.y) / run, z: fin(t.z) / run });
  };
  const cell = (cu: CellSheetUniforms, e: 'mid' | 'low', c: CellDrive, sc: { cellsM: number; lowM: number; detailM: number; warpM: number }): void => {
    const p = c.p, dd = c.d;
    set4(cu.geo, c.c0, c.c1, d.rcKm, p.thickM);
    set4(cu.org, c.org[0], c.org[1], c.cw, c.sw);
    set4(cu.pat, p.wPads, p.wWaves, p.wFbm, p.wLow);
    set4(cu.thr, dd.thr, dd.thrPatch, dd.span, dd.farHgt);
    set4(cu.shp, p.flat, p.gamma, p.detail, sc.warpM);
    set4(cu.scl, 1 / sc.cellsM, 1 / sc.cellsM, 1 / sc.lowM, 1 / sc.detailM);
    // opt.y gates the étage (an absent one costs nothing); sun.z thins what is drawn
    set4(cu.opt, dd.tauEff, fin(c.cover * c.presence), p.fwd, p.side);
    set4(cu.sun, c.sun[0], c.sun[1], fin(c.presence), fin(dd.edge)); // sun.w: THE FLAT FLOOR'S EDGE shift
    light(cu, e, p.baseM, c01(c.cover) * c01(c.cover) * c.presence, dd.meanB);
    // the deck under the rain dome carries the dome's own grey (the weather tint is the mesh clouds' albedo)
    set3(cu.tint, tint);
  };
  cell(u.low, 'low', d.low, LOW_SCALES);
  cell(u.mid, 'mid', d.mid, MID_SCALES);
  const h = d.high, hp = h.p, hu = u.high;
  set4(hu.geo, h.c0, h.c1, d.rcKm, hp.thickM);
  set4(hu.org, h.org[0], h.org[1], h.cw, h.sw);
  set4(hu.pat, hp.wFil, hp.wHook, hp.wStreak, hp.veil);
  set4(hu.tau, hp.tauCi, hp.tauCs, hp.tauCc, hp.halo);
  set4(hu.ptc, 1 - c01(hp.patchCover), 0.3, HIGH_SCALES.warpM, h.presence > 0 && h.cover > 0 ? 1 : 0);
  set4(hu.cc, hp.ccW, hp.ripple, 1 - CC_GRAIN_COVER, 1 - c01(hp.ccCover));
  set4(hu.scl, 1 / HIGH_SCALES.alongM, 1 / HIGH_SCALES.acrossM, 1 / HIGH_SCALES.lowM, 1 / HIGH_SCALES.ccM);
  // opt.y: the étage's presence (rain, the approach, a genus leaving) scales every density; opt.z: the grains' far mean
  set4(hu.opt, hp.fwd, fin(h.presence), CC_GRAIN_COVER / 1.8, 0);
  light(hu, 'high', hp.baseM, 0, 0);
}
/** The air's transmittance toward a direction of elevation sine `sinEl` from `altKm` (lib/sky/sky_sampler's `transmittance`). */
export interface SheetSampler { transmittance(sinEl: number, altKm?: number): V3 }
export interface SheetFrame {
  dt: number; spec: SheetSpec; seed?: number; snap?: boolean;
  cam: V3; wind: { x: number; z: number } | null;
  /** The frame's light (SheetLightFrame less the transmittances — `driveSheets` asks the sampler). */
  light: Omit<SheetLightFrame, 'tSun' | 'tGround'>;
  sampler: SheetSampler;
  /** The droplets' albedo for the low and middle étages (the weather's tint). */
  tint?: V3;
}
const tSunOf = { high: { x: 1, y: 1, z: 1 }, mid: { x: 1, y: 1, z: 1 }, low: { x: 1, y: 1, z: 1 } } as Record<Etage, V3>;
/**
 * One frame, whole: ease the runtime toward the spec, light the étages, write the uniforms. SkyModule and the bed both
 * call THIS, so the bed pictures the game's own drive. Returns the drive and the lights (the lever's report).
 */
export function driveSheets(rt: SheetRuntime, u: CloudSheetUniforms, fr: SheetFrame): { drive: SheetDrive; lights: Record<Etage, EtageLight> } {
  const L = fr.light, S = fr.sampler;
  const camKm = Math.max(0.001, fin(fr.cam.y) / 1000);
  // the key the slab's sunward tap follows: the low étage's (the sun, or the moon once it outshines it)
  const probe: SheetLightFrame = { ...L, tSun: tSunOf, tGround: tSunOf.low };
  const env: SheetEnv = { cam: fr.cam, wind: fr.wind, cloudiness: L.cloudiness, visMix: L.visMix, keyDir: sheetLight(probe, 'low', 1000).keyDir };
  rt.step(fr.dt, fr.spec, env, fr.seed ?? 0, !!fr.snap);
  const drive = rt.drive(env);
  const alt: Record<Etage, number> = { high: drive.high.p.baseM, mid: drive.mid.p.baseM, low: drive.low.p.baseM };
  const frame: SheetLightFrame = {
    ...L, tGround: S.transmittance(L.sinEl),
    tSun: { high: S.transmittance(L.sinEl, alt.high / 1000), mid: S.transmittance(L.sinEl, alt.mid / 1000), low: S.transmittance(L.sinEl, alt.low / 1000) },
  };
  const lights = sheetLights(frame, drive);
  // the clear air between the eye and each étage: ln T(zenith, étage) − ln T(zenith, eye)
  const t0 = S.transmittance(1, camKm);
  const tauTo = (altM: number): V3 => {
    const t = S.transmittance(1, Math.max(camKm, altM / 1000));
    const d = (a: number, b: number): number => Math.max(0, Math.log(Math.max(1e-9, a)) - Math.log(Math.max(1e-9, b)));
    return { x: d(t.x, t0.x), y: d(t.y, t0.y), z: d(t.z, t0.z) };
  };
  writeSheetUniforms(u, drive, lights, { tauTo: { high: tauTo(alt.high), mid: tauTo(alt.mid), low: tauTo(alt.low) }, camY: fin(fr.cam.y) }, fr.tint);
  return { drive, lights };
}

/**
 * The share of the lightning CHANNEL's held radiance the low deck's flash takes at its centre (the deck is a diffuser
 * lit by the stroke, not the stroke). ≈ 0.35 of the channel on a storm deck after the shader's own deck term.
 * UNCALIBRATED — no photograph or measurement stands behind 0.6; only the LAW below does.
 */
export const SHEET_FLASH_SHARE = 0.6;
/**
 * The flash's radiance (scene units) for `setSheetFlash`: `channelScene` is the lightning channel on the exposure curve —
 * lib/vfx/fire_rules `emitterScene(EMITTER_NITS.lightning, EMITTER_DISPLAY_MAX.lightning, exposureState)`, the bolt's own
 * scale — × the stroke's envelope 0..1 × the strike's distance falloff (AtlasLightningModule's 1 / (1 + 0.45 km)).
 * THE FLASH IS AN EMITTER (FIX 2026-10-07): it was first scaled by the deck's OWN radiance, so it was proportional to the
 * ambient light — fine at noon and BLACK at midnight (the bed's night flash frame was identical to the unlit one),
 * the reverse of lightning. On the curve it is a modest patch against a noon overcast and a white one at night.
 */
export function sheetFlashRadiance(channelScene: number, envelope: number, km: number): number {
  return SHEET_FLASH_SHARE * Math.max(0, fin(channelScene)) * c01(envelope) / (1 + 0.45 * Math.max(0, fin(km)));
}
/** The low deck's lightning flash: the strike's point relative to the camera (m, world XZ), a radius (m) and a radiance (scene units; 0 = off — `sheetFlashRadiance`). */
export function setSheetFlash(u: CloudSheetUniforms, relX: number, relZ: number, radiusM: number, intensity: number): void {
  set4(u.low.flash, fin(relX), fin(relZ), 1 / Math.max(1, radiusM * radiusM), Math.max(0, fin(intensity)));
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
// 10. The graph (TSL)
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════

/** Henyey–Greenstein, per steradian. */
function hg(cosT: N, g: number): N {
  return float((1 - g * g) / (4 * Math.PI)).div(pow(max(float(1 + g * g).sub(cosT.mul(2 * g)), float(1e-4)), float(1.5)));
}
/** The forward lobes around the key as τ·e^(−τ): the corona (a ground-glass sun), the silver region, a wide skirt. */
function forwardLobes(cosT: N, tau: N): N {
  const lobe = (g: number, w: number, s: number): N => { const t = tau.mul(s); return hg(cosT, g).mul(w).mul(t).mul(exp(t.negate())); };
  return lobe(0.975, 0.22, 0.5).add(lobe(0.86, 0.45, 0.7)).add(lobe(0.45, 0.33, 0.35));
}
/** B(τ) (deckBrightness) in TSL. */
function brightnessNode(tau: N): N {
  return float(1).sub(exp(tau.div(-TAU_I))).div(tau.div(TAU_HALF).add(1));
}
/** The SOURCE of an element whose column above is τ: B(τ) per unit opacity — 1/3.5 for a wisp, ~0.45 at τ 3–10, falling as the column thickens. */
function sourceNode(tau: N): N {
  const t = max(tau, float(1e-3));
  return brightnessNode(t).div(float(1).sub(exp(t.negate())));
}

interface Shell { t: N; dt: N }
/** A ray of elevation sine `dy` meets the shell c (km²): the slant distance (km) and its derivative in dy. */
function shellNode(c: N, rc: N, dy: N): Shell {
  const b = rc.mul(dy);
  const s = sqrt(max(b.mul(b).sub(c), float(1e-6)));
  const t = c.negate().div(max(b.add(s), float(1e-6)));
  return { t, dt: rc.mul(t).div(s).negate() };
}

interface Frame { d: N; ddx: N; ddy: N }
/** The pattern-frame point at a slant distance, and the shell point's screen derivatives (the textureGrad footprint). */
function patternPoint(f: Frame, org: N, tKm: N, dtKm: N): { q: N; dqx: N; dqy: N; rel: N } {
  const cw = org.z, sw = org.w;
  const rot = (v: N): N => vec2(v.x.mul(cw).add(v.y.mul(sw)), v.y.mul(cw).sub(v.x.mul(sw)));
  const h = vec2(f.d.x, f.d.z);
  const hr = rot(h);
  const rel = hr.mul(tKm.mul(1000));
  // d(h·t)/dx = dh/dx · t + h · t′(dy) · d(dy)/dx — analytic, so it is defined everywhere (no derivative of a branch)
  const dqx = rot(vec2(f.ddx.x, f.ddx.z)).mul(tKm).add(hr.mul(dtKm.mul(f.ddx.y))).mul(1000);
  const dqy = rot(vec2(f.ddy.x, f.ddy.z)).mul(tKm).add(hr.mul(dtKm.mul(f.ddy.y))).mul(1000);
  return { q: vec2(org.x, org.y).add(rel), dqx, dqy, rel };
}

/** The elevation sine the geometry is taken at: never under the geometric horizon (the deck's own horizon colour runs on below it). */
function horizonClamp(dy: N, lu: LightUniforms): N { return max(dy, lu.misc.w.add(0.003)); }
/** The composite of one étage over what is behind it: Beer through the cloud, then the air's path to it. */
function composite(behind: N, rgbCloud: N, alpha: N, dy: N, dyc: N, tKm: N, lu: LightUniforms): N {
  const Tr = exp(vec3(lu.hazeK).mul(tKm).negate());
  const up = max(dyc, float(0));
  // the air between the eye and the layer: the sky's own colour there by day, the étage's own horizon under a deck
  const inscat = mix(behind, vec3(lu.hazeRgb).mul(up.mul(2).add(1)), lu.misc.z);
  const seen = behind.mul(alpha.oneMinus()).add(rgbCloud);
  // the layer ends a little UNDER the geometric horizon (the ground covers that): no seam of clear sky at the horizon line
  const mask = smoothstep(lu.misc.w.sub(0.03), lu.misc.w.sub(0.004), dy);
  return mix(behind, seen.mul(Tr).add(inscat.mul(Tr.oneMinus())), mask);
}

/** A low / middle étage over `behind` (plain function — called inside the top `Fn`, under the étage's uniform `If`). */
function cellSheet(behind: N, f: Frame, tex: SheetTextures, u: CellSheetUniforms, segments: number, sunward: boolean): N {
  const dy = horizonClamp(f.d.y, u);
  const s0 = shellNode(u.geo.x, u.geo.z, dy), s1 = shellNode(u.geo.y, u.geo.z, dy);
  const tm = s0.t.add(s1.t).mul(0.5), dtm = s0.dt.add(s1.dt).mul(0.5);
  const m = patternPoint(f, u.org, tm, dtm);
  const sC = vec2(u.scl.x, u.scl.y);
  const gx = m.dqx.mul(sC), gy = m.dqy.mul(sC);
  // one low-frequency fetch of the smooth fields: the warp (r, g), the patch field (b), the large mottling (a)
  const low = texture(tex.smooth, m.q.mul(u.scl.z)).grad(m.dqx.mul(u.scl.z), m.dqy.mul(u.scl.z)).toVar();
  const det = texture(tex.cells, m.q.mul(u.scl.w)).grad(m.dqx.mul(u.scl.w), m.dqy.mul(u.scl.w)).b.toVar();
  const warp = vec2(low.r, low.g).sub(0.5).mul(u.shp.w);
  const thr = u.thr.x.sub(low.b.sub(0.5).mul(u.thr.y));
  const add = det.sub(0.5).mul(u.shp.z).add(low.a.sub(0.5).mul(u.pat.w));
  const hgtAt = (q: N): N => {
    const c = texture(tex.cells, q.add(warp).mul(sC)).grad(gx, gy);
    const pat = c.r.mul(u.pat.x).add(c.a.mul(u.pat.y)).add(c.g.mul(u.pat.z)).add(add);
    // THE FLAT FLOOR HAS AN EDGE (cellThickness is the mirror): the floor only where the pattern covers
    const x = saturate(pat.sub(thr).div(u.thr.z));
    return u.shp.x.mul(smoothstep(float(0), float(FLAT_EDGE_W), x.add(u.sun.w))).add(u.shp.x.oneMinus().mul(pow(x, u.shp.y)));
  };
  const cw = u.org.z, sw = u.org.w;
  const hr = vec2(f.d.x.mul(cw).add(f.d.z.mul(sw)), f.d.z.mul(cw).sub(f.d.x.mul(sw)));
  // THE FOOTPRINT: past ~⅓ cell a pixel the thresholded field is its mean — and so it is once the slab's segments
  // step further apart than the cells (a ray that crosses several elements meets the field's mean, not three points of it)
  const fp = max(length(gx), length(gy)).mul(SHEET_CELLS);
  const stride = length(hr).mul(s1.t.sub(s0.t)).mul(1000 / segments).mul(u.scl.x).mul(SHEET_CELLS);
  const blur = max(smoothstep(0.3, 1.1, fp), smoothstep(0.45, 1.3, stride)).toVar();
  const path = max(s1.t.sub(s0.t).mul(1000), u.geo.w);               // metres through the slab
  const muL = u.geo.w.div(path);                                    // the layer's own zenith cosine
  const scale = u.opt.x.mul(u.sun.z);                               // optical depth of a full column × the étage's presence
  // THE SLAB, front to back: the height field h(q) against the ray's own height z, h linear between the segment ends.
  // The share of a segment inside the cloud is (g₀⁺ + g₁⁺)/(|g₀| + |g₁|), g = h − z (exact for a column; no ghost copies
  // of an edge). What a segment SHOWS is the element it enters: its source is B of the column ABOVE the entry point —
  // the whole column for a base, a thin one for a side met above a gap (a side is brighter than a base). Integrating
  // the brightness along the path instead smears every pad radially (a zoom blur about the zenith).
  const g: N[] = [];
  for (let i = 0; i <= segments; i++) g.push(hgtAt(vec2(u.org.x, u.org.y).add(hr.mul(mix(s0.t, s1.t, i / segments).mul(1000)))).sub(i / segments).toVar());
  const T = float(1).toVar(), lum = float(0).toVar(), inside = float(0).toVar();
  const dTau = scale.div(muL).div(segments);
  for (let i = 0; i < segments; i++) {
    const a = max(g[i], float(0)), b = max(g[i + 1], float(0));
    const frac = a.add(b).div(max(g[i].abs().add(g[i + 1].abs()), float(1e-4)));
    const e = exp(frac.mul(dTau).negate());
    lum.addAssign(T.mul(e.oneMinus()).mul(sourceNode(mix(b.mul(0.5), a, a.greaterThan(1e-5).toFloat()).mul(scale))));
    T.mulAssign(e);
    inside.addAssign(frac);
  }
  // the far field: the mean depth, and a brightness that keeps the base's relief (a far deck is a quilt, not a fog)
  const tauFar = u.thr.w.mul(scale).div(muL);
  const aFar = float(1).sub(exp(tauFar.negate()));
  const alpha = mix(T.oneMinus(), aFar, blur).toVar();
  const tau = mix(inside.div(segments).mul(scale).div(muL), tauFar, blur);
  let lit: N = mix(lum, aFar.mul(sourceNode(mix(u.thr.w, max(g[0], float(0)), 0.5).mul(scale))), blur);
  // the sunward tap: thicker toward the key = this side is the shaded one; thinner = the lit edge
  if (sunward) lit = lit.mul(mix(clamp(exp(hgtAt(vec2(u.org.x, u.org.y).add(hr.mul(s0.t.mul(1000))).add(vec2(u.sun.x, u.sun.y))).sub(max(g[0], float(0))).mul(scale).mul(-0.03)), 0.6, 1.5), float(1), blur));
  const up = max(dy, float(0));
  const cosT = dot(f.d, vec3(u.keyDir));
  // LIT FROM ABOVE, SEEN FROM BELOW: the overcast law × the source; the side toward the antisolar horizon is the lit one
  const kh = vec2(u.keyDir.x, u.keyDir.z), vh = vec2(f.d.x, f.d.z);
  const sideLit = float(1).sub(dot(vh, kh).mul(u.opt.w).mul(up.oneMinus()));
  const diff = vec3(u.eDiff).mul(up.mul(2).add(1).div(3)).mul(lit).mul(sideLit);
  const fwd = vec3(u.sunL).mul(forwardLobes(cosT, tau).mul(u.opt.z));
  const night = vec3(u.night).mul(u.misc.x.add(u.misc.y.mul(up))).mul(alpha);
  // the flash: a patch of the deck around the strike, brightest where the deck is thin enough to pass it
  const fd = m.rel.sub(vec2(u.flash.x.mul(cw).add(u.flash.y.mul(sw)), u.flash.y.mul(cw).sub(u.flash.x.mul(sw))));
  const flash = vec3(u.flashRgb).mul(u.flash.w).mul(exp(dot(fd, fd).mul(u.flash.z).negate())).mul(lit.mul(4).add(alpha.mul(0.3)));
  const rgb = diff.add(fwd).mul(vec3(u.tint)).add(night).add(flash);
  return composite(behind, rgb, alpha, f.d.y, dy, tm, u);
}

/** The high étage over `behind`. */
function fibreSheet(behind: N, f: Frame, tex: SheetTextures, u: FibreSheetUniforms): N {
  const dy = horizonClamp(f.d.y, u);
  const s0 = shellNode(u.geo.x, u.geo.z, dy), s1 = shellNode(u.geo.y, u.geo.z, dy);
  const m = patternPoint(f, u.org, s0.t, s0.dt);
  // one low-frequency fetch of the smooth fields: the bend of the whole flow (r, g) and the patches (b; a for the grains)
  const low = texture(tex.smooth, m.q.mul(u.scl.z)).grad(m.dqx.mul(u.scl.z), m.dqy.mul(u.scl.z)).toVar();
  const sF = vec2(u.scl.x, u.scl.y);
  const qf = m.q.add(vec2(low.r, low.g).sub(0.5).mul(u.ptc.z));
  const fb = texture(tex.fibres, qf.mul(sF)).grad(m.dqx.mul(sF), m.dqy.mul(sF)).toVar();
  const fine = texture(tex.fibres, qf.mul(sF).mul(4)).grad(m.dqx.mul(sF).mul(4), m.dqy.mul(sF).mul(4)).a.toVar();
  const patch = smoothstep(u.ptc.x.sub(u.ptc.y), u.ptc.x.add(u.ptc.y), low.b);
  const strands = fb.r.mul(u.pat.x).add(fb.g.mul(u.pat.y));
  const ci = patch.mul(strands.mul(fine.mul(0.4).add(0.8)).add(fb.b.mul(fb.b).mul(u.pat.z).mul(fine.mul(0.3).add(0.35)).mul(0.5)));
  const cs = u.pat.w.mul(fb.b.mul(0.3).add(0.7)).mul(fine.mul(0.16).add(0.92));
  const tauV = ci.mul(u.tau.x).add(cs.mul(u.tau.y)).toVar();
  // cirrocumulus: grains and ripples in their own patches — fetched only when the genus is in the sky (a uniform branch)
  If(u.cc.x.greaterThan(0), () => {
    const sG = u.scl.w;
    const g = texture(tex.cells, m.q.mul(sG)).grad(m.dqx.mul(sG), m.dqy.mul(sG));
    const pat = mix(g.r, g.a, u.cc.y);
    const fp = max(length(m.dqx), length(m.dqy)).mul(sG).mul(SHEET_CELLS);
    const grain = mix(pow(saturate(pat.sub(u.cc.z).div(float(1).sub(u.cc.z))), float(0.8)), u.opt.z, smoothstep(0.3, 1.1, fp));
    tauV.addAssign(grain.mul(smoothstep(u.cc.w.sub(0.2), u.cc.w.add(0.2), low.a)).mul(u.cc.x).mul(u.tau.z));
  });
  tauV.mulAssign(u.opt.y);
  const air = max(s1.t.sub(s0.t).mul(1000), u.geo.w).div(u.geo.w);  // the slant factor through the sheet
  const tau = tauV.mul(air).toVar();
  const alpha = float(1).sub(exp(tau.negate()));
  const up = max(dy, float(0));
  const cosT = dot(f.d, vec3(u.keyDir));
  const diff = vec3(u.eDiff).mul(up.mul(2).add(1).div(3)).mul(brightnessNode(tauV));
  // the 22° halo of hexagonal columns: a sharp red inner edge, a soft bluish outer one
  const ang = acos(clamp(cosT, -1, 1)).mul(180 / Math.PI);
  const ring = (c: number, wIn: number, wOut: number): N => { const x = ang.sub(c); return exp(x.mul(x).div(mix(float(wIn * wIn), float(wOut * wOut), smoothstep(-0.2, 0.2, x))).negate()); };
  const halo = vec3(ring(21.7, 0.5, 2.6), ring(22.0, 0.5, 3.2), ring(22.3, 0.55, 3.9)).mul(u.tau.w).mul(0.22);
  const fwd = vec3(u.sunL).mul(vec3(forwardLobes(cosT, tau).mul(u.opt.x)).add(halo.mul(tau).mul(exp(tau.negate()))));
  const night = vec3(u.night).mul(u.misc.x.add(u.misc.y.mul(up))).mul(alpha);
  return composite(behind, diff.add(fwd).add(night), alpha, f.d.y, dy, s0.t, u);
}

export interface SheetBuildOptions {
  /** Slab segments for the low and middle étages, and the sunward thickness tap (sheetQuality(tier)). */
  low: number; mid: number; sunward: boolean;
}
/**
 * The sky with the three sheets over it: `baseRgb` (the atmosphere + night + moon, scene units) → vec3. Build it ONCE
 * (the composite); everything it reads is a uniform. An étage whose cover uniform is 0 costs nothing.
 */
export function buildCloudSheets(viewDir: N, baseRgb: N, tex: SheetTextures, u: CloudSheetUniforms, o: SheetBuildOptions): N {
  return Fn(() => {
    const out = vec3(baseRgb).toVar('sheetSky');
    const d = vec3(viewDir).toVar('sheetDir');
    // THE LAZY-VAR LAW: the view's screen derivatives once, before any branch
    const f: Frame = { d, ddx: dFdx(d).toVar('sheetDx'), ddy: dFdy(d).toVar('sheetDy') };
    If(u.high.ptc.w.greaterThan(0), () => { out.assign(fibreSheet(out, f, tex, u.high)); });
    If(u.mid.opt.y.greaterThan(0), () => { out.assign(cellSheet(out, f, tex, u.mid, Math.max(1, Math.round(o.mid)), o.sunward)); });
    If(u.low.opt.y.greaterThan(0), () => { out.assign(cellSheet(out, f, tex, u.low, Math.max(1, Math.round(o.low)), o.sunward)); });
    return out;
  })();
}

// keep `min` and `vec4` reachable for callers composing with this graph (tree-shaken otherwise)
export const _tsl = { min, vec4 };
